import React, { Fragment, useState, useCallback, useEffect, useRef } from 'react';
import { Container, TabContent, TabPane, Nav, NavItem, NavLink, Card, CardBody, Modal, ModalHeader, ModalBody, ModalFooter, Row, Col, FormGroup, Label, Input } from 'reactstrap';
import { Breadcrumbs, Btn } from '../../../AbstractElements';
import classnames from 'classnames';
import TodoListListing from './TodoListListing';
import { TodoCategoriesList } from './TodoListCategories';
import { Plus, Info, Edit, Info as InfoIcon, Lock, DollarSign, FileText, UserPlus, UserX, X } from 'react-feather';
import { fetchTableData, fetchFormData, requestNewTask, fetchAccidentFilesReadyForArbitration, fetchClosedAccidentFiles, closeAccidentFile, assignToSelf, releaseFromSelf, assignArbitrationToSelf, releaseArbitrationFromSelf } from '../../../Api/ApiMethods';
import { toast } from 'react-toastify';
import DataTable from 'react-data-table-component';
import { useNavigate } from 'react-router-dom';
import ExpenseDataModal from '../FinancialOperations/Expenses/ExpenseDataModal';
import FileCloseModal from '../Arbitrations/ArbitrationDetails/FileCloseModal';

// Tab information for info modal
const tabInfo = {
    '1': {
        title: 'Tüm İşler Sekmesi',
        color: 'blue',
        content: 'Bu sekme tüm işleri gösterir.'
    },
    '2': {
        title: 'Yeni Eklenenler Sekmesi',
        color: 'red',
        content: 'Bu sekme yeni eklenen işleri gösterir.'
    },
    '3': {
        title: 'Yapılacaklar Sekmesi',
        color: 'orange',
        content: 'Bu sekme yapılacakları gösterir.'
    },
    '4': {
        title: 'Devam Edenler Sekmesi',
        color: 'cyan',
        content: 'Bu sekme devam eden işleri gösterir.'
    },
    '5': {
        title: 'Tamamlananlar Sekmesi',
        color: 'green',
        content: 'Bu sekme tamamlanan işleri gösterir.'
    },
    '6': {
        title: 'Çalışılmayanlar Sekmesi',
        color: 'dark',
        content: 'Bu sekme çalışılmayan işleri gösterir.'
    },
    '7': {
        title: 'Tahkime Hazırlar Sekmesi',
        color: 'purple',
        content: 'Bu sekme tahkime hazır kaza dosyalarını gösterir.'
    },
    '8': {
        title: 'Tahkimdekiler Sekmesi',
        color: 'info',
        content: 'Bu sekme tahkimde bulunan dosyaları gösterir.'
    },
    '9': {
        title: 'Kapalı Dosyalar Sekmesi',
        color: 'secondary',
        content: 'Bu sekme kapatılmış kaza dosyalarını gösterir.'
    }
};

// Rol bazlı görünür sekme yapılandırması
const roleTabConfig = {
    'CALL CENTER': {
        visibleTabIndices: [1, 2],
        canRequestTask: true,
        taskType: 'callCenter'
    },
    'HEAD CALL CENTER': {
        visibleTabIndices: [1, 2],
        canRequestTask: true,
        taskType: 'headCallCenter'
    },
    'APPRAISER': {
        visibleTabIndices: [2],
        canRequestTask: true,
        taskType: 'appraiser'
    },
    'HEAD APPRAISER': {
        visibleTabIndices: [2],
        canRequestTask: true,
        taskType: 'headAppraiser'
    },
    'ACCOUNTING': {
        visibleTabIndices: [2],
        canRequestTask: false
    },
    "HEAD ACCOUNTING": {
        visibleTabIndices: [2],
        canRequestTask: false
    },
    'POWER OF ATTORNEY REGISTRATION': {
        visibleTabIndices: [2],
        canRequestTask: false
    },
    'DOCUMENT REQUEST': {
        visibleTabIndices: [2],
        canRequestTask: false
    },
    'INSURANCE': {
        visibleTabIndices: [2],
        canRequestTask: false
    },
    'LAWYER': {
        visibleTabIndices: [2],
        canRequestTask: false
    },
    'ARBITRATION LAWYER': {
        visibleTabIndices: [6, 7, 8],
        canRequestTask: false
    },
    'MANAGEMENT': {
        visibleTabIndices: [0/*, 4*/, 5, 6, 7, 8],
        canRequestTask: false
    },
    'COORDINATOR': {
        visibleTabIndices: [0/*, 4*/, 5, 6, 7, 8],
        canRequestTask: false
    },
    'ADMIN': {
        visibleTabIndices: [0/*, 4*/, 5, 6, 7, 8],
        canRequestTask: false
    },
    // Default configuration (show all, no task request)
    'DEFAULT': { visibleTabIndices: [0, 1, 2, 3, 4, 5, 6, 7, 8], canRequestTask: false }
};

// Helper function to get current role configuration
const getCurrentRoleConfig = () => {
    const currentUserRole = localStorage.getItem('userRole');
    return roleTabConfig[currentUserRole] || roleTabConfig['DEFAULT'];
};

// Helper function to get visible tabs based on current role
const getVisibleTabs = () => {
    const userRoleConfig = getCurrentRoleConfig();
    return TodoCategoriesList.filter((category, index) =>
        userRoleConfig.visibleTabIndices.includes(index)
    );
};

// Helper function to get default active tab ID
const getDefaultActiveTabId = () => {
    const visibleTabs = getVisibleTabs();
    return visibleTabs.length > 0
        ? (TodoCategoriesList.findIndex(cat => cat.title === visibleTabs[0].title) + 1).toString()
        : '1';
};

const TodoList = () => {
    const navigate = useNavigate();

    // Refs to track initial load for each tab to prevent unnecessary API calls
    const isTab7InitialLoad = useRef(true);
    const isTab8InitialLoad = useRef(true);
    const isTab9InitialLoad = useRef(true);

    // State for role-based configuration that updates dynamically
    const [userRoleConfig, setUserRoleConfig] = useState(getCurrentRoleConfig());
    const [visibleTabs, setVisibleTabs] = useState(getVisibleTabs());
    const [canRequestTask, setCanRequestTask] = useState(userRoleConfig.canRequestTask);

    const [activeTab, setActiveTab] = useState(getDefaultActiveTabId());
    const [isInfoOpen, setIsInfoOpen] = useState(false);
    const [ownTasksItems, setOwnTasksItems] = useState([]);
    const [isOwnTasksLoading, setIsOwnTasksLoading] = useState(false);
    const [ownTasksTotalRows, setOwnTasksTotalRows] = useState(0); // Count for Tab 2
    const [ownTasksPage, setOwnTasksPage] = useState(1);
    const [ownTasksPageSize, setOwnTasksPageSize] = useState(10);
    const [maxTaskCount, setMaxTaskCount] = useState(0);
    const [todayTaskCount, setTodayTaskCount] = useState(0);
    const [isCountLoading, setIsCountLoading] = useState(false);
    const [isAssigningTask, setIsAssigningTask] = useState(false);
    const [isReleasingTask, setIsReleasingTask] = useState(false);
    const [listRefreshKey, setListRefreshKey] = useState(0);

    // State to hold the count reported by the active TodoListListing
    const [activeListCount, setActiveListCount] = useState(0);
    // State to track if the active list is loading (optional, for badge display)
    const [isActiveListLoading, setIsActiveListLoading] = useState(false);

    const [appraiserTaskCount, setAppraiserTaskCount] = useState(0);
    const [poolTaskCount, setPoolTaskCount] = useState(0);
    const [isAppraiserCountLoading, setIsAppraiserCountLoading] = useState(false);

    // State for Tahkime Hazırlar tab (Tab 7)
    const [arbitrationItems, setArbitrationItems] = useState([]);
    const [isArbitrationLoading, setIsArbitrationLoading] = useState(false);
    const [arbitrationTotalRows, setArbitrationTotalRows] = useState(0);
    const [arbitrationPage, setArbitrationPage] = useState(1);
    const [arbitrationPageSize, setArbitrationPageSize] = useState(10);

    // New filters for Tab 7 - replace old search with specific filters
    const [applicationDateSearchTab7, setApplicationDateSearchTab7] = useState('');
    const [accidentDateSearchTab7, setAccidentDateSearchTab7] = useState('');
    const [tcIdSearchTab7, setTcIdSearchTab7] = useState('');

    // State for Dosya Kapa Modal (Tab 7)
    const [isCloseFileModalOpen, setIsCloseFileModalOpen] = useState(false);
    const [currentCloseFileId, setCurrentCloseFileId] = useState(null);
    const [fileStatus, setFileStatus] = useState('Kapalı');
    const [fileCloseDescription, setFileCloseDescription] = useState('');
    const [fileCloseComment, setFileCloseComment] = useState('');
    const [isClosingFile, setIsClosingFile] = useState(false);

    // State for Başvuru Ücreti Modal (Tab 7)
    const [isApplicationFeeModalOpen, setIsApplicationFeeModalOpen] = useState(false);
    const [currentApplicationFileId, setCurrentApplicationFileId] = useState(null);
    const [arbitrationApplicationFee, setArbitrationApplicationFee] = useState('');
    const [isSubmittingApplicationFee, setIsSubmittingApplicationFee] = useState(false);

    // State for Tahkimdekiler tab (Tab 8)
    const [arbitrationActiveItems, setArbitrationActiveItems] = useState([]);
    const [isArbitrationActiveLoading, setIsArbitrationActiveLoading] = useState(false);
    const [arbitrationActiveTotalRows, setArbitrationActiveTotalRows] = useState(0);
    const [arbitrationActivePage, setArbitrationActivePage] = useState(1);
    const [arbitrationActivePageSize, setArbitrationActivePageSize] = useState(10);
    const [caseNumberSearch, setCaseNumberSearch] = useState('');

    // New filters for Tab 8
    const [tcIdSearch, setTcIdSearch] = useState('');
    const [accidentDateSearch, setAccidentDateSearch] = useState('');

    // State for FileCloseModal (Tab 8)
    const [isFileCloseModalOpen, setIsFileCloseModalOpen] = useState(false);
    const [currentArbitrationIdForClose, setCurrentArbitrationIdForClose] = useState(null);

    // State for Arbitration State Filter (Tab 8)
    const [arbitrationStates, setArbitrationStates] = useState({});
    const [arbitrationStatesList, setArbitrationStatesList] = useState([]);
    const [selectedArbitrationStateFilter, setSelectedArbitrationStateFilter] = useState('');

    // State for Arbitration Expense Modal (Tab 8)
    const [isArbitrationExpenseModalOpen, setIsArbitrationExpenseModalOpen] = useState(false);
    const [currentArbitrationItem, setCurrentArbitrationItem] = useState(null);

    // State for Tahkim Bilgileri Modal (Tab 8)
    const [isArbitrationInfoModalOpen, setIsArbitrationInfoModalOpen] = useState(false);
    const [currentArbitrationInfoItem, setCurrentArbitrationInfoItem] = useState(null);
    const [arbitrationApplicationDate, setArbitrationApplicationDate] = useState('');
    const [arbitrationCaseNumber, setArbitrationCaseNumber] = useState('');
    const [arbitrationPriceForNow, setArbitrationPriceForNow] = useState('');
    const [isSubmittingArbitrationInfo, setIsSubmittingArbitrationInfo] = useState(false);

    // State for Kapalı Dosyalar tab (Tab 9)
    const [closedFilesItems, setClosedFilesItems] = useState([]);
    const [isClosedFilesLoading, setIsClosedFilesLoading] = useState(false);
    const [closedFilesTotalRows, setClosedFilesTotalRows] = useState(0);
    const [closedFilesPage, setClosedFilesPage] = useState(1);
    const [closedFilesPageSize, setClosedFilesPageSize] = useState(10);

    // New filters for Tab 9 - replace old search with specific filters
    const [applicationDateSearchTab9, setApplicationDateSearchTab9] = useState('');
    const [accidentDateSearchTab9, setAccidentDateSearchTab9] = useState('');
    const [tcIdSearchTab9, setTcIdSearchTab9] = useState('');
    const [fileCloseCauseSearchTab9, setFileCloseCauseSearchTab9] = useState('');

    // Effect to listen for role changes and update configuration
    useEffect(() => {
        const handleRoleChange = () => {
            const newRoleConfig = getCurrentRoleConfig();
            const newVisibleTabs = getVisibleTabs();
            const newDefaultActiveTab = getDefaultActiveTabId();

            setUserRoleConfig(newRoleConfig);
            setVisibleTabs(newVisibleTabs);
            setCanRequestTask(newRoleConfig.canRequestTask);

            // Reset to the first visible tab for the new role
            setActiveTab(newDefaultActiveTab);
            setActiveListCount(0);
            setIsActiveListLoading(true);

            // Reset other states
            setOwnTasksPage(1);
            setArbitrationPage(1);
            setApplicationDateSearchTab7(''); // Reset Tab 7 filters
            setAccidentDateSearchTab7('');
            setTcIdSearchTab7('');
            setCaseNumberSearch(''); // Reset case number search
            setTcIdSearch(''); // Reset TC ID search
            setAccidentDateSearch(''); // Reset accident date search
            setApplicationDateSearchTab9(''); // Reset Tab 9 filters
            setAccidentDateSearchTab9('');
            setTcIdSearchTab9('');
            setFileCloseCauseSearchTab9('');
            setListRefreshKey(prev => prev + 1);

            console.log('TodoList: Role changed, updated configuration', {
                role: localStorage.getItem('userRole'),
                visibleTabs: newVisibleTabs.map(tab => tab.title),
                canRequestTask: newRoleConfig.canRequestTask
            });
        };

        // Listen for role change events
        window.addEventListener('userRoleChanged', handleRoleChange);

        return () => {
            window.removeEventListener('userRoleChanged', handleRoleChange);
        };
    }, []);

    // Reset ref flags when activeTab changes to prevent stale initial load flags
    useEffect(() => {
        console.log('🔄 TAB CHANGED: Resetting initial load flags for activeTab:', activeTab);

        // Reset all flags except the current active tab
        if (activeTab !== '7') {
            isTab7InitialLoad.current = true;
        }
        if (activeTab !== '8') {
            isTab8InitialLoad.current = true;
        }
        if (activeTab !== '9') {
            isTab9InitialLoad.current = true;
        }
    }, [activeTab]);

    // useEffect to fetch Arbitration States for Tab 8 filter
    useEffect(() => {
        const fetchArbitrationStates = async () => {
            console.log("Fetching arbitration states...");
            try {
                const response = await fetchFormData({
                    apiEndpoint: process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/ArbitrationState',
                    page: 1,
                    pageSize: 100 // Fetch all states
                });
                console.log("API Response for arbitration states:", response);

                let allItems = [];
                if (response && response.data && Array.isArray(response.data.items)) {
                    allItems = response.data.items;
                } else if (response && Array.isArray(response.items)) {
                    allItems = response.items;
                } else if (response && Array.isArray(response.data)) {
                    allItems = response.data;
                } else {
                    console.warn("Unexpected API response structure for arbitration states:", response);
                }

                if (allItems.length > 0) {
                    // Create a state map for quick lookup
                    const statesMap = {};
                    allItems.forEach(state => {
                        if (state.id && state.title) {
                            statesMap[state.id] = state.title;
                        }
                    });
                    setArbitrationStates(statesMap);
                    setArbitrationStatesList(allItems);
                    console.log("Arbitration states loaded:", { statesMap, allItems });
                } else {
                    console.warn("No arbitration states found in API response");
                    setArbitrationStates({});
                    setArbitrationStatesList([]);
                }
            } catch (error) {
                console.error('Error fetching arbitration states:', error);
                setArbitrationStates({});
                setArbitrationStatesList([]);
            }
        };

        fetchArbitrationStates();
    }, []);

    // --- accidentFileColumns definition for Tab 7 (Tahkime Hazırlar) ---
    const accidentFileColumns = [
        {
            name: 'Müşteri',
            selector: row => {
                const customer = row.customer;
                if (!customer) return '-';

                // Kurumsal veya Bireysel kontrol
                if (customer.customerType === 'Kurumsal') {
                    return customer.companyName || '-';
                } else {
                    return customer.clientName || '-';
                }
            },
            sortable: true,
            grow: 2, // En büyük alan
            minWidth: '200px'
        },
        {
            name: 'TC/Vergi No',
            selector: row => {
                const customer = row.customer;
                if (!customer) return '-';

                // Kurumsal veya Bireysel kontrol
                if (customer.customerType === 'Kurumsal') {
                    return customer.taxNumber || '-';
                } else {
                    return customer.identityNumber || '-';
                }
            },
            sortable: true,
            grow: 1,
            minWidth: '120px'
        },
        {
            name: 'Kaza Dosyası No',
            selector: row => row.fileNumber || '-',
            sortable: true,
            grow: 1,
            minWidth: '120px'
        },
        {
            name: 'Plaka',
            selector: row => row.accident?.licensePlate?.licensePlateNumber || '-',
            sortable: true,
            grow: 1,
            minWidth: '100px'
        },
        {
            name: 'Başvuru Tarihi',
            selector: row => row.insuranceApplicationDate ? new Date(row.insuranceApplicationDate).toLocaleDateString('tr-TR') : '-',
            sortable: true,
            grow: 1,
            minWidth: '110px'
        },
        {
            name: 'Kaza Tarihi',
            selector: row => row.accident?.accidentDate ? new Date(row.accident.accidentDate).toLocaleDateString('tr-TR') : '-',
            sortable: true,
            grow: 1,
            minWidth: '110px'
        },
        {
            name: 'Talep Edilen İş',
            selector: row => {
                const filesToBeWorked = row.filesToBeWorked || [];
                if (filesToBeWorked.length === 0) return '-';

                const fileTypes = filesToBeWorked
                    .map(file => {
                        const fileTypeArray = file.fileType || [];
                        return fileTypeArray.map(ft => ft.title || '').filter(title => title !== '');
                    })
                    .flat()
                    .join(', ');

                return fileTypes || '-';
            },
            sortable: true,
            grow: 1.5,
            minWidth: '150px'
        },
        {
            name: 'Çalışılan Tutar',
            selector: row => {
                const amountWorked = row.amountWorked;
                return amountWorked ? `${amountWorked.toLocaleString('tr-TR')} ₺` : '-';
            },
            sortable: true,
            grow: 1,
            minWidth: '120px'
        },
        {
            name: 'Başvuru Ücreti',
            cell: (row) => (
                <Btn
                    attrBtn={{
                        color: 'success',
                        size: 'sm',
                        outline: true,
                        onClick: () => openApplicationFeeModal(row.id),
                        title: 'Başvuru Ücreti Gir'
                    }}
                >
                    <DollarSign size={18} />
                </Btn>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            grow: 1,
            minWidth: '110px',
            center: true
        },
        {
            name: 'Dosya Kapa',
            cell: (row) => (
                <Btn
                    attrBtn={{
                        color: 'danger',
                        size: 'sm',
                        outline: true,
                        onClick: () => openCloseFileModal(row.id),
                        title: 'Dosya Kapa'
                    }}
                >
                    <Lock size={18} />
                </Btn>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            grow: 1,
            minWidth: '110px',
            center: true,
            right: true
        }
    ];

    // --- closedFilesColumns definition for Tab 9 (Kapalı Dosyalar) ---
    const closedFilesColumns = [
        {
            name: 'Müşteri',
            selector: row => {
                const customer = row.customer;
                if (!customer) return '-';

                // Kurumsal veya Bireysel kontrol
                if (customer.customerType === 'Kurumsal') {
                    return customer.companyName || '-';
                } else {
                    return customer.clientName || '-';
                }
            },
            sortable: true,
            grow: 2, // En büyük alan
            minWidth: '200px'
        },
        {
            name: 'TC/Vergi No',
            selector: row => {
                const customer = row.customer;
                if (!customer) return '-';

                // Kurumsal veya Bireysel kontrol
                if (customer.customerType === 'Kurumsal') {
                    return customer.taxNumber || '-';
                } else {
                    return customer.identityNumber || '-';
                }
            },
            sortable: true,
            grow: 1,
            minWidth: '120px'
        },
        {
            name: 'Kaza Dosyası No',
            selector: row => row.fileNumber || '-',
            sortable: true,
            grow: 1,
            minWidth: '120px'
        },
        {
            name: 'Plaka',
            selector: row => row.accident?.licensePlate?.licensePlateNumber || '-',
            sortable: true,
            grow: 1,
            minWidth: '100px'
        },
        {
            name: 'Başvuru Tarihi',
            selector: row => row.insuranceApplicationDate ? new Date(row.insuranceApplicationDate).toLocaleDateString('tr-TR') : '-',
            sortable: true,
            grow: 1,
            minWidth: '110px'
        },
        {
            name: 'Kaza Tarihi',
            selector: row => row.accident?.accidentDate ? new Date(row.accident.accidentDate).toLocaleDateString('tr-TR') : '-',
            sortable: true,
            grow: 1,
            minWidth: '110px'
        },
        {
            name: 'Talep Edilen İş',
            selector: row => {
                const filesToBeWorked = row.filesToBeWorked || [];
                if (filesToBeWorked.length === 0) return '-';

                const fileTypes = filesToBeWorked
                    .map(file => {
                        const fileTypeArray = file.fileType || [];
                        return fileTypeArray.map(ft => ft.title || '').filter(title => title !== '');
                    })
                    .flat()
                    .join(', ');

                return fileTypes || '-';
            },
            sortable: true,
            grow: 1.5,
            minWidth: '150px'
        },
        {
            name: 'Çalışılan Tutar',
            selector: row => {
                const amountWorked = row.amountWorked;
                return amountWorked ? `${amountWorked.toLocaleString('tr-TR')} ₺` : '-';
            },
            sortable: true,
            grow: 1,
            minWidth: '120px'
        },
        {
            name: 'Durum',
            selector: row => row.fileStatus || 'Kapalı',
            sortable: true,
            grow: 0.8,
            minWidth: '80px'
        },
        {
            name: 'Kapanma Açıklaması',
            selector: row => row.fileCloseDescription || '-',
            sortable: true,
            grow: 2,
            minWidth: '150px'
        },
        {
            name: 'Tahkim Detay',
            cell: (row) => {
                // Arbitration ID'si varsa edit butonunu göster
                if (row.arbitration && row.arbitration.length > 0) {
                    return (
                        <Btn
                            attrBtn={{
                                color: 'primary',
                                size: 'sm',
                                outline: true,
                                onClick: () => navigate(`${process.env.PUBLIC_URL}/arbitration/${row.arbitration[0]}`),
                                title: 'Tahkim Detaylarını Görüntüle'
                            }}
                        >
                            <Edit size={18} />
                        </Btn>
                    );
                } else {
                    return '-';
                }
            },
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            grow: 1,
            minWidth: '110px',
            center: true
        }
    ];

    // --- arbitrationActiveColumns definition for Tab 8 (Tahkimdekiler) ---
    const arbitrationActiveColumns = [
        {
            name: 'Müşteri',
            selector: row => {
                const customer = row.accidentFile?.customer;
                if (!customer) return '-';

                // Kurumsal veya Bireysel kontrol
                if (customer.customerType === 'Kurumsal') {
                    return customer.companyName || '-';
                } else {
                    return customer.clientName || '-';
                }
            },
            sortable: true,
            grow: 2,
            minWidth: '200px'
        },
        {
            name: 'TC/Vergi No',
            selector: row => {
                const customer = row.accidentFile?.customer;
                if (!customer) return '-';

                // Kurumsal veya Bireysel kontrol
                if (customer.customerType === 'Kurumsal') {
                    return customer.taxNumber || '-';
                } else {
                    return customer.identityNumber || '-';
                }
            },
            sortable: true,
            grow: 1,
            minWidth: '120px'
        },
        {
            name: 'Kaza Dosyası No',
            selector: row => row.accidentFile?.fileNumber || '-',
            sortable: true,
            grow: 1,
            minWidth: '120px'
        },
        {
            name: 'Esas Numarası',
            selector: row => row.arbitrationInformation?.caseNumber || '-',
            sortable: true,
            grow: 1,
            minWidth: '120px'
        },
        {
            name: 'Plaka',
            selector: row => row.accidentFile?.accident?.licensePlate?.licensePlateNumber || '-',
            sortable: true,
            grow: 1,
            minWidth: '100px'
        },
        {
            name: 'Kaza Tarihi',
            selector: row => row.accidentFile?.accident?.accidentDate ? new Date(row.accidentFile.accident.accidentDate).toLocaleDateString('tr-TR') : '-',
            sortable: true,
            grow: 1,
            minWidth: '110px'
        },
        {
            name: 'Talep Edilen İş',
            selector: row => {
                const filesToBeWorked = row.accidentFile?.filesToBeWorked || [];
                if (filesToBeWorked.length === 0) return '-';

                const fileTypes = filesToBeWorked
                    .map(file => {
                        const fileTypeArray = file.fileType || [];
                        return fileTypeArray.map(ft => ft.title || '').filter(title => title !== '');
                    })
                    .flat()
                    .join(', ');

                return fileTypes || '-';
            },
            sortable: true,
            grow: 1.5,
            minWidth: '150px'
        },
        {
            name: 'Çalışılan Tutar',
            selector: row => {
                const amountWorked = row.accidentFile?.amountWorked;
                return amountWorked ? `${amountWorked.toLocaleString('tr-TR')} ₺` : '-';
            },
            sortable: true,
            grow: 1,
            minWidth: '120px'
        },
        {
            name: 'Başvuru Ücreti',
            selector: row => {
                const applicationFee = row.arbitrationInformation?.arbitrationApplicationFee;
                return applicationFee ? `${applicationFee.toLocaleString('tr-TR')} ₺` : '-';
            },
            sortable: true,
            grow: 1,
            minWidth: '120px'
        },
        {
            name: 'Tahkim Durumu',
            selector: row => row.arbitrationState?.title || '-',
            sortable: true,
            grow: 2.5,
            minWidth: '200px'
        },
        {
            name: 'Kendine Ata/Kaldır',
            cell: (row) => {
                // Eğer appointed user yoksa, kendine atama butonu göster
                if (!row.appointedUser) {
                    return (
                        <Btn
                            attrBtn={{
                                color: 'outline-primary',
                                size: 'sm',
                                onClick: () => handleArbitrationAssignToSelf(row.id),
                                title: 'Kendine Ata',
                                disabled: isAssigningTask
                            }}
                        >
                            <UserPlus size={18} />
                        </Btn>
                    );
                }

                // Eğer appointed user mevcut kullanıcı ise, kendinden kaldırma butonu göster
                if (row.canRelease) {
                    return (
                        <Btn
                            attrBtn={{
                                color: 'outline-danger',
                                size: 'sm',
                                onClick: () => handleArbitrationReleaseFromSelf(row.id),
                                title: 'Kendinden Kaldır',
                                disabled: isReleasingTask
                            }}
                        >
                            <UserX size={18} />
                        </Btn>
                    );
                }

                // Başka bir kullanıcıya atanmış ise hiçbir buton gösterme
                return null;
            },
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: '150px',
        },
        {
            name: 'İşlemler',
            cell: row => {
                const arbitrationState = row.arbitrationState?.title;

                // Tahkim Başvuru Ücreti Ödeme Bekleniyor durumunda sadece Başvuru Ücreti butonu
                if (arbitrationState === 'Tahkim Başvuru Ücreti Ödeme Bekleniyor' && row.canEdit === true) {
                    return (
                        <Btn
                            attrBtn={{
                                color: 'outline-success',
                                size: 'sm',
                                onClick: () => handleArbitrationExpenseModal(row),
                                title: 'Başvuru Ücreti'
                            }}
                        >
                            <DollarSign size={14} />
                        </Btn>
                    );
                }

                // Tahkim Başvuru Bekleniyor durumunda sadece Tahkim Bilgileri butonu
                if (arbitrationState === 'Tahkim Başvuru Bekleniyor' && row.canEdit === true) {
                    return (
                        <Btn
                            attrBtn={{
                                color: 'outline-info',
                                size: 'sm',
                                onClick: () => openArbitrationInfoModal(row),
                                title: 'Tahkim Bilgileri Gir'
                            }}
                        >
                            <FileText size={14} />
                        </Btn>
                    );
                }

                // Diğer tüm durumlarda sadece Düzenle butonu
                if (row.canEdit === true) {
                    return (
                        <Btn
                            attrBtn={{
                                color: 'outline-warning',
                                size: 'sm',
                                onClick: () => handleArbitrationEdit(row),
                                title: 'Düzenle'
                            }}
                        >
                            <Edit size={14} />
                        </Btn>
                    );
                }
            },
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: '120px',
        },
        {
            name: 'Kapat',
            cell: row => {
                const currentState = row.arbitrationState?.title;
                // Tekrar Başvuru Bekleniyor ve Kapama Bekleniyor durumlarında gösterme
                const shouldShow = !['Tekrar Başvuru Bekleniyor', 'Kapama Bekleniyor'].includes(currentState);

                if (shouldShow) {
                    return (
                        <Btn
                            attrBtn={{
                                color: 'outline-primary',
                                size: 'sm',
                                onClick: () => handleFileCloseModal(row),
                                title: 'Dosya Kapat'
                            }}
                        >
                            <Lock size={14} />
                        </Btn>
                    );
                }
                return null;
            },
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: '80px',
        }
    ];

    // --- dataPoolColumns definition ---
    const dataPoolColumns = [
        { name: 'Plaka', selector: row => row.plate, sortable: true },
        { name: 'Şase Numarası', selector: row => row.chassisNumber, sortable: true },
        { name: 'Kaza Tarihi', selector: row => row.accidentDate ? new Date(row.accidentDate).toLocaleString() : '-', sortable: true },
        { name: 'Rapor Tarihi', selector: row => row.reportDate ? new Date(row.reportDate).toLocaleString() : '-', sortable: true },
        { name: 'TC Kimlik Numarası', selector: row => row.tcid, sortable: true },
        { name: 'Ad Soyad', selector: row => row.fullName, sortable: true },
        { name: 'Kaynak Klasör', selector: row => row.originFolder || '-', sortable: true },
        {
            name: 'Telefon', selector: row => {
                if (Array.isArray(row.phone) && row.phone.length > 0) {
                    const firstPhoneObj = row.phone[0];
                    if (firstPhoneObj && typeof firstPhoneObj === 'object' && firstPhoneObj.hasOwnProperty('phone') && firstPhoneObj.phone) {
                        const firstPhone = firstPhoneObj.phone;
                        if (row.phone.length > 1) {
                            const validPhonesCount = row.phone.filter(p => p && p.phone && p.phone.trim() !== '').length;
                            if (validPhonesCount > 1) {
                                const remainingCount = validPhonesCount - 1;
                                return `${firstPhone} +${remainingCount}`;
                            }
                        }
                        return firstPhone;
                    }
                }
                return '-';
            }, sortable: true
        },
        { name: 'Kusur Durumu', selector: row => (row.faultStatus !== null && row.faultStatus !== undefined ? `${row.faultStatus}%` : '-'), sortable: true },
        {
            name: 'Çağrı Formu',
            cell: (row) => (
                <Btn
                    attrBtn={{
                        color: 'primary',
                        size: 'sm',
                        outline: true,
                        onClick: () => {
                            if (row.id) {
                                navigate(`/callform/new-callform/${row.id}`);
                            } else {
                                console.error("Satır için geçerli ID bulunamadı:", row);
                                toast.error("Bu kayıt için form oluşturulamıyor (ID eksik).");
                            }
                        }
                    }}
                >
                    <Edit size={18} />
                </Btn>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            right: true,
        }
    ];
    // --- dataPoolColumns end ---

    // Callback function for TodoListListing to report its count
    const handleListCountChange = useCallback((count) => {
        // Update count only if the tab providing the count is currently active
        // This prevents inactive tabs from overwriting the active count.
        // We might need to pass the tabId to TodoListListing or find another way
        // to associate the count if multiple listings could potentially exist.
        // For now, let's assume only the active tab's listing calls this.
        setActiveListCount(count);
        setIsActiveListLoading(false); // Assume loading finished when count arrives
        console.log("Active list count updated:", count);
    }, []);

    const toggle = tab => {
        const targetCategory = TodoCategoriesList[parseInt(tab, 10) - 1];
        const isVisible = visibleTabs.some(cat => cat.title === targetCategory?.title);
        if (isVisible && activeTab !== tab) {
            setActiveTab(tab);
            setActiveListCount(0); // Reset count when switching tabs
            setIsActiveListLoading(true); // Set loading for the new active tab
            if (tab === '2') {
                setOwnTasksPage(1); // Reset pagination for own tasks tab
            }
            if (tab === '7') {
                setArbitrationPage(1); // Reset pagination for arbitration tab
                setApplicationDateSearchTab7(''); // Reset Tab 7 filters
                setAccidentDateSearchTab7('');
                setTcIdSearchTab7('');
            }
            if (tab === '8') {
                setArbitrationActivePage(1); // Reset pagination for arbitration active tab
                setCaseNumberSearch(''); // Reset case number search for arbitration active tab
                setTcIdSearch(''); // Reset TC ID search
                setAccidentDateSearch(''); // Reset accident date search
                setSelectedArbitrationStateFilter(''); // Reset filter for arbitration active tab
            }
            if (tab === '9') {
                setClosedFilesPage(1); // Reset pagination for closed files tab
                setApplicationDateSearchTab9(''); // Reset Tab 9 filters
                setAccidentDateSearchTab9('');
                setTcIdSearchTab9('');
                setFileCloseCauseSearchTab9('');
            }
        }
    };

    const toggleInfo = () => setIsInfoOpen(!isInfoOpen);

    // Function to fetch Own Tasks data (Tab 2)
    const fetchOwnTasks = useCallback(async () => {
        const apiEndpoint = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/datapool/get-own';
        if (!apiEndpoint) {
            toast.error("Own tasks API endpoint is not configured.");
            return;
        }
        setIsOwnTasksLoading(true);
        try {
            await fetchTableData({
                apiEndpoint: apiEndpoint,
                page: ownTasksPage,
                pageSize: ownTasksPageSize,
                search: '',
                setDataCallback: setOwnTasksItems,
                setTotalRowsCallback: setOwnTasksTotalRows,
            });
        } catch (error) {
            console.error("Error calling fetchTableData for Own Tasks:", error);
            setOwnTasksTotalRows(0); // Set count to 0 on error
        } finally {
            setIsOwnTasksLoading(false);
        }
    }, [ownTasksPage, ownTasksPageSize]);

    // Function to fetch Accident Files Ready for Arbitration data (Tab 7)
    const fetchArbitrationData = useCallback(async () => {
        console.log('🚀 fetchArbitrationData CALLED (Tab 7):', {
            applicationDateSearchTab7,
            accidentDateSearchTab7,
            tcIdSearchTab7,
            arbitrationPage,
            arbitrationPageSize,
            activeTab,
            caller: 'SEARCH/DEBOUNCE',
            timestamp: new Date().toISOString(),
            stackTrace: new Error().stack?.split('\n').slice(1, 5)
        });

        const apiEndpoint = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/AccidentFile';
        if (!apiEndpoint) {
            toast.error("Accident files API endpoint is not configured.");
            return;
        }

        setIsArbitrationLoading(true);
        try {
            const queryParams = new URLSearchParams();

            // Başvuru tarihi filtresi
            if (applicationDateSearchTab7 && applicationDateSearchTab7.trim().length > 0) {
                queryParams.append('applicationDate', applicationDateSearchTab7);
            }

            // Kaza tarihi filtresi
            if (accidentDateSearchTab7 && accidentDateSearchTab7.trim().length > 0) {
                queryParams.append('accidentDate', accidentDateSearchTab7);
            }

            // TC ID filtresi - en az 3 karakter gerekli
            if (tcIdSearchTab7 && tcIdSearchTab7.trim().length >= 3) {
                queryParams.append('tcId', tcIdSearchTab7.trim());
            }

            const fullEndpoint = queryParams.toString() ? `${apiEndpoint}?${queryParams.toString()}` : apiEndpoint;

            console.log('📡 API REQUEST (Tab 7):', {
                endpoint: fullEndpoint,
                applicationDateSearchTab7,
                accidentDateSearchTab7,
                tcIdSearchTab7,
                queryParams: queryParams.toString(),
                page: arbitrationPage,
                pageSize: arbitrationPageSize
            });

            await fetchAccidentFilesReadyForArbitration({
                apiEndpoint: fullEndpoint,
                page: arbitrationPage,
                pageSize: arbitrationPageSize,
                search: null, // Remove old general search
                setDataCallback: setArbitrationItems,
                setTotalRowsCallback: setArbitrationTotalRows,
            });
        } catch (error) {
            console.error("Error calling fetchAccidentFilesReadyForArbitration:", error);
            setArbitrationTotalRows(0); // Set count to 0 on error
        } finally {
            setIsArbitrationLoading(false);
        }
    }, [arbitrationPage, arbitrationPageSize, applicationDateSearchTab7, accidentDateSearchTab7, tcIdSearchTab7]);

    // Separate function for tab/pagination changes only (without search dependency) - Tab 7
    const fetchArbitrationDataNonSearch = useCallback(async () => {
        console.log('🚀 fetchArbitrationDataNonSearch CALLED (pagination/tab only - Tab 7):', {
            arbitrationPage,
            arbitrationPageSize,
            activeTab,
            caller: 'PAGINATION/TAB_CHANGE',
            timestamp: new Date().toISOString(),
            stackTrace: new Error().stack?.split('\n').slice(1, 5)
        });

        const apiEndpoint = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/AccidentFile';
        if (!apiEndpoint) {
            toast.error("Accident files API endpoint is not configured.");
            return;
        }

        setIsArbitrationLoading(true);
        try {
            const queryParams = new URLSearchParams();

            // For non-search calls, use current search states but don't depend on them
            const currentApplicationDateSearch = applicationDateSearchTab7;
            const currentAccidentDateSearch = accidentDateSearchTab7;
            const currentTcIdSearch = tcIdSearchTab7;

            // Başvuru tarihi filtresi
            if (currentApplicationDateSearch && currentApplicationDateSearch.trim().length > 0) {
                queryParams.append('applicationDate', currentApplicationDateSearch);
            }

            // Kaza tarihi filtresi
            if (currentAccidentDateSearch && currentAccidentDateSearch.trim().length > 0) {
                queryParams.append('accidentDate', currentAccidentDateSearch);
            }

            // TC ID filtresi - en az 3 karakter gerekli
            if (currentTcIdSearch && currentTcIdSearch.trim().length >= 3) {
                queryParams.append('tcId', currentTcIdSearch.trim());
            }

            const fullEndpoint = queryParams.toString() ? `${apiEndpoint}?${queryParams.toString()}` : apiEndpoint;

            console.log('📡 API REQUEST (Tab 7 - NON-SEARCH):', {
                endpoint: fullEndpoint,
                applicationDateSearchTab7: currentApplicationDateSearch,
                accidentDateSearchTab7: currentAccidentDateSearch,
                tcIdSearchTab7: currentTcIdSearch,
                queryParams: queryParams.toString(),
                page: arbitrationPage,
                pageSize: arbitrationPageSize
            });

            await fetchAccidentFilesReadyForArbitration({
                apiEndpoint: fullEndpoint,
                page: arbitrationPage,
                pageSize: arbitrationPageSize,
                search: null, // Remove old general search
                setDataCallback: setArbitrationItems,
                setTotalRowsCallback: setArbitrationTotalRows,
            });
        } catch (error) {
            console.error("Error calling fetchAccidentFilesReadyForArbitration (non-search):", error);
            setArbitrationTotalRows(0); // Set count to 0 on error
        } finally {
            setIsArbitrationLoading(false);
        }
    }, [arbitrationPage, arbitrationPageSize]); // NO search dependencies

    // Function to fetch Active Arbitrations data (Tab 8)
    const fetchArbitrationActiveData = useCallback(async () => {
        console.log('🚀 fetchArbitrationActiveData CALLED:', {
            caseNumberSearch,
            tcIdSearch,
            accidentDateSearch,
            selectedArbitrationStateFilter,
            arbitrationActivePage,
            arbitrationActivePageSize,
            activeTab,
            caller: 'SEARCH/DEBOUNCE',
            timestamp: new Date().toISOString(),
            stackTrace: new Error().stack?.split('\n').slice(1, 5)
        });

        const apiEndpoint = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/Arbitration';
        if (!apiEndpoint) {
            console.error("API endpoint is not configured for arbitration active data.");
            return;
        }

        setIsArbitrationActiveLoading(true);
        try {
            const queryParams = new URLSearchParams();

            // Esas numarası aramasi - en az 3 karakter gerekli
            const hasCaseNumberSearch = caseNumberSearch && caseNumberSearch.trim().length >= 3;
            if (hasCaseNumberSearch) {
                queryParams.append('caseNumber', caseNumberSearch.trim());
            }

            // TC ID ve Kaza Tarihi aramaları - esas numarası yokken kullanılır
            const hasTcIdSearch = tcIdSearch && tcIdSearch.trim().length >= 3; // En az 3 karakter gerekli
            const hasAccidentDateSearch = accidentDateSearch && accidentDateSearch.trim().length > 0;

            if (!hasCaseNumberSearch) {
                if (hasTcIdSearch) {
                    queryParams.append('tcId', tcIdSearch.trim());
                }
                if (hasAccidentDateSearch) {
                    queryParams.append('accidentDate', accidentDateSearch);
                }
            }

            // Tahkim durumu filtresi - sadece esas numarası, TC ID veya kaza tarihi aramasi yokken kullanılır
            if (!hasCaseNumberSearch && !hasTcIdSearch && !hasAccidentDateSearch && selectedArbitrationStateFilter && selectedArbitrationStateFilter !== '') {
                queryParams.append('stateId', selectedArbitrationStateFilter);
            }

            const fullEndpoint = queryParams.toString() ? `${apiEndpoint}?${queryParams.toString()}` : apiEndpoint;

            console.log('📡 API REQUEST:', {
                endpoint: fullEndpoint,
                caseNumberSearch: caseNumberSearch,
                hasCaseNumberSearch,
                tcIdSearch: tcIdSearch,
                hasTcIdSearch,
                accidentDateSearch: accidentDateSearch,
                hasAccidentDateSearch,
                selectedArbitrationStateFilter,
                queryParams: queryParams.toString(),
                page: arbitrationActivePage,
                pageSize: arbitrationActivePageSize
            });

            await fetchTableData({
                apiEndpoint: fullEndpoint,
                page: arbitrationActivePage,
                pageSize: arbitrationActivePageSize,
                search: null,
                setDataCallback: setArbitrationActiveItems,
                setTotalRowsCallback: setArbitrationActiveTotalRows,
            });
        } catch (error) {
            console.error("Error fetching arbitration active data:", error);
            setArbitrationActiveItems([]);
            setArbitrationActiveTotalRows(0);
        } finally {
            setIsArbitrationActiveLoading(false);
        }
    }, [arbitrationActivePage, arbitrationActivePageSize, caseNumberSearch, tcIdSearch, accidentDateSearch, selectedArbitrationStateFilter]);

    // Separate function for tab/pagination changes only (without search dependency)
    const fetchArbitrationActiveDataNonSearch = useCallback(async () => {
        console.log('🚀 fetchArbitrationActiveDataNonSearch CALLED (pagination/filter only):', {
            selectedArbitrationStateFilter,
            arbitrationActivePage,
            arbitrationActivePageSize,
            activeTab,
            caller: 'PAGINATION/TAB_CHANGE/FILTER',
            timestamp: new Date().toISOString(),
            stackTrace: new Error().stack?.split('\n').slice(1, 5)
        });

        const apiEndpoint = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/Arbitration';
        if (!apiEndpoint) {
            console.error("API endpoint is not configured for arbitration active data.");
            return;
        }

        setIsArbitrationActiveLoading(true);
        try {
            const queryParams = new URLSearchParams();

            // For non-search calls, use current search states but don't depend on them
            const currentCaseSearch = caseNumberSearch;
            const currentTcIdSearch = tcIdSearch;
            const currentAccidentDateSearch = accidentDateSearch;

            const hasCaseNumberSearch = currentCaseSearch && currentCaseSearch.trim().length >= 3;
            if (hasCaseNumberSearch) {
                queryParams.append('caseNumber', currentCaseSearch.trim());
            }

            // TC ID ve Kaza Tarihi aramaları - esas numarası yokken kullanılır
            const hasTcIdSearch = currentTcIdSearch && currentTcIdSearch.trim().length >= 3; // En az 3 karakter gerekli
            const hasAccidentDateSearch = currentAccidentDateSearch && currentAccidentDateSearch.trim().length > 0;

            if (!hasCaseNumberSearch) {
                if (hasTcIdSearch) {
                    queryParams.append('tcId', currentTcIdSearch.trim());
                }
                if (hasAccidentDateSearch) {
                    queryParams.append('accidentDate', currentAccidentDateSearch);
                }
            }

            // Tahkim durumu filtresi - sadece diğer aramalar yokken kullanılır
            if (!hasCaseNumberSearch && !hasTcIdSearch && !hasAccidentDateSearch && selectedArbitrationStateFilter && selectedArbitrationStateFilter !== '') {
                queryParams.append('stateId', selectedArbitrationStateFilter);
            }

            const fullEndpoint = queryParams.toString() ? `${apiEndpoint}?${queryParams.toString()}` : apiEndpoint;

            console.log('📡 API REQUEST (NON-SEARCH):', {
                endpoint: fullEndpoint,
                caseNumberSearch: currentCaseSearch,
                hasCaseNumberSearch,
                tcIdSearch: currentTcIdSearch,
                hasTcIdSearch,
                accidentDateSearch: currentAccidentDateSearch,
                hasAccidentDateSearch,
                selectedArbitrationStateFilter,
                queryParams: queryParams.toString(),
                page: arbitrationActivePage,
                pageSize: arbitrationActivePageSize
            });

            await fetchTableData({
                apiEndpoint: fullEndpoint,
                page: arbitrationActivePage,
                pageSize: arbitrationActivePageSize,
                search: null,
                setDataCallback: setArbitrationActiveItems,
                setTotalRowsCallback: setArbitrationActiveTotalRows,
            });
        } catch (error) {
            console.error("Error fetching arbitration active data (non-search):", error);
            setArbitrationActiveItems([]);
            setArbitrationActiveTotalRows(0);
        } finally {
            setIsArbitrationActiveLoading(false);
        }
    }, [arbitrationActivePage, arbitrationActivePageSize, selectedArbitrationStateFilter]); // NO search dependencies

    // Function to fetch Closed Files data (Tab 9)
    const fetchClosedFilesData = useCallback(async () => {
        console.log('🚀 fetchClosedFilesData CALLED (Tab 9):', {
            applicationDateSearchTab9,
            accidentDateSearchTab9,
            tcIdSearchTab9,
            closedFilesPage,
            closedFilesPageSize,
            activeTab,
            caller: 'SEARCH/DEBOUNCE',
            timestamp: new Date().toISOString(),
            stackTrace: new Error().stack?.split('\n').slice(1, 5)
        });

        const apiEndpoint = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/accidentfile';
        if (!apiEndpoint) {
            console.error("API endpoint is not configured for closed files data.");
            return;
        }

        setIsClosedFilesLoading(true);
        try {
            const queryParams = new URLSearchParams();

            // Başvuru tarihi filtresi
            if (applicationDateSearchTab9 && applicationDateSearchTab9.trim().length > 0) {
                queryParams.append('applicationDate', applicationDateSearchTab9);
            }

            // Kaza tarihi filtresi
            if (accidentDateSearchTab9 && accidentDateSearchTab9.trim().length > 0) {
                queryParams.append('accidentDate', accidentDateSearchTab9);
            }

            // TC ID filtresi - en az 3 karakter gerekli
            if (tcIdSearchTab9 && tcIdSearchTab9.trim().length >= 3) {
                queryParams.append('tcId', tcIdSearchTab9.trim());
            }

            // Kapanma Açıklaması filtresi
            if (fileCloseCauseSearchTab9 && fileCloseCauseSearchTab9.trim().length > 0) {
                queryParams.append('fileCloseCause', fileCloseCauseSearchTab9.trim());
            }

            const fullEndpoint = queryParams.toString() ? `${apiEndpoint}?${queryParams.toString()}` : apiEndpoint;

            console.log('📡 API REQUEST (Tab 9):', {
                endpoint: fullEndpoint,
                applicationDateSearchTab9,
                accidentDateSearchTab9,
                tcIdSearchTab9,
                fileCloseCauseSearchTab9,
                queryParams: queryParams.toString(),
                page: closedFilesPage,
                pageSize: closedFilesPageSize
            });

            await fetchClosedAccidentFiles({
                apiEndpoint: fullEndpoint,
                page: closedFilesPage,
                pageSize: closedFilesPageSize,
                search: null, // Remove old general search
                setDataCallback: setClosedFilesItems,
                setTotalRowsCallback: setClosedFilesTotalRows,
            });
        } catch (error) {
            console.error("Error calling fetchClosedAccidentFiles:", error);
            setClosedFilesItems([]);
            setClosedFilesTotalRows(0);
        } finally {
            setIsClosedFilesLoading(false);
        }
    }, [closedFilesPage, closedFilesPageSize, applicationDateSearchTab9, accidentDateSearchTab9, tcIdSearchTab9, fileCloseCauseSearchTab9]);

    // Separate function for tab/pagination changes only (without search dependency) - Tab 9
    const fetchClosedFilesDataNonSearch = useCallback(async () => {
        console.log('🚀 fetchClosedFilesDataNonSearch CALLED (pagination/tab only - Tab 9):', {
            closedFilesPage,
            closedFilesPageSize,
            activeTab,
            caller: 'PAGINATION/TAB_CHANGE',
            timestamp: new Date().toISOString(),
            stackTrace: new Error().stack?.split('\n').slice(1, 5)
        });

        const apiEndpoint = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/accidentfile';
        if (!apiEndpoint) {
            console.error("API endpoint is not configured for closed files data.");
            return;
        }

        setIsClosedFilesLoading(true);
        try {
            const queryParams = new URLSearchParams();

            // For non-search calls, use current search states but don't depend on them
            const currentApplicationDateSearch = applicationDateSearchTab9;
            const currentAccidentDateSearch = accidentDateSearchTab9;
            const currentTcIdSearch = tcIdSearchTab9;

            // Başvuru tarihi filtresi
            if (currentApplicationDateSearch && currentApplicationDateSearch.trim().length > 0) {
                queryParams.append('applicationDate', currentApplicationDateSearch);
            }

            // Kaza tarihi filtresi
            if (currentAccidentDateSearch && currentAccidentDateSearch.trim().length > 0) {
                queryParams.append('accidentDate', currentAccidentDateSearch);
            }

            // TC ID filtresi - en az 3 karakter gerekli
            if (currentTcIdSearch && currentTcIdSearch.trim().length >= 3) {
                queryParams.append('tcId', currentTcIdSearch.trim());
            }

            const fullEndpoint = queryParams.toString() ? `${apiEndpoint}?${queryParams.toString()}` : apiEndpoint;

            console.log('📡 API REQUEST (Tab 9 - NON-SEARCH):', {
                endpoint: fullEndpoint,
                applicationDateSearchTab9: currentApplicationDateSearch,
                accidentDateSearchTab9: currentAccidentDateSearch,
                tcIdSearchTab9: currentTcIdSearch,
                queryParams: queryParams.toString(),
                page: closedFilesPage,
                pageSize: closedFilesPageSize
            });

            await fetchClosedAccidentFiles({
                apiEndpoint: fullEndpoint,
                page: closedFilesPage,
                pageSize: closedFilesPageSize,
                search: null, // Remove old general search
                setDataCallback: setClosedFilesItems,
                setTotalRowsCallback: setClosedFilesTotalRows,
            });
        } catch (error) {
            console.error("Error calling fetchClosedAccidentFiles (non-search):", error);
            setClosedFilesItems([]);
            setClosedFilesTotalRows(0);
        } finally {
            setIsClosedFilesLoading(false);
        }
    }, [closedFilesPage, closedFilesPageSize]); // NO search dependencies

    // Debouncing effect for Tab 7 search fields - wait 500ms after user stops typing + initial load
    useEffect(() => {
        console.log('🕐 DEBOUNCE TAB 7 USEEFFECT START:', {
            activeTab,
            applicationDateSearchTab7,
            accidentDateSearchTab7,
            tcIdSearchTab7,
            tcIdLength: tcIdSearchTab7.trim().length,
            timestamp: new Date().toISOString(),
            stackTrace: new Error().stack?.split('\n').slice(1, 4)
        });

        if (activeTab === '7') { // Only for Tab 7 (Tahkime Hazırlar)
            // Check if this is initial load for this tab
            if (isTab7InitialLoad.current && activeTab === '7') {
                console.log('⏸️ DEBOUNCE TAB 7: Initial load, skipping API call');
                isTab7InitialLoad.current = false;
                return;
            }

            // Check if any search field has content OR if fields were just cleared
            const hasAnySearch = applicationDateSearchTab7.trim() || accidentDateSearchTab7.trim() || tcIdSearchTab7.trim();

            console.log('✅ DEBOUNCE TAB 7: Active tab is 7, setting timeout');
            const timeoutId = setTimeout(() => {
                console.log('⏰ DEBOUNCE TAB 7 TIMEOUT EXECUTED:', {
                    applicationDateSearchTab7,
                    accidentDateSearchTab7,
                    tcIdSearchTab7,
                    tcIdLength: tcIdSearchTab7.trim().length,
                    hasAnySearch,
                    willCallAPI: true
                });

                if (hasAnySearch) {
                    // TC ID varsa en az 3 karakter olmalı, yoksa API çağrısı yapmayalım
                    const hasTcIdSearch = tcIdSearchTab7 && tcIdSearchTab7.trim().length > 0;
                    const hasValidTcIdSearch = tcIdSearchTab7 && tcIdSearchTab7.trim().length >= 3;

                    // Eğer TC ID girilmişse ama 3 karakterden azsa API çağrısı yapma
                    if (hasTcIdSearch && !hasValidTcIdSearch) {
                        console.log('⏸️ DEBOUNCE TAB 7 TIMEOUT: TC ID too short, skipping API call');
                        return;
                    }

                    // Call fetchArbitrationData for search changes
                    console.log('🔄 DEBOUNCE TAB 7: Calling fetchArbitrationData (with search)');
                    fetchArbitrationData();
                } else {
                    // All fields are empty, call NonSearch to get unfiltered data
                    console.log('� DEBOUNCE TAB 7: Calling fetchArbitrationDataNonSearch (fields cleared)');
                    fetchArbitrationDataNonSearch();
                }
            }, 500); // 500ms delay for all searches

            return () => {
                console.log('🚫 DEBOUNCE TAB 7: Timeout cleared');
                clearTimeout(timeoutId);
            };
        } else {
            console.log('❌ DEBOUNCE TAB 7: Active tab is not 7, skipping');
        }
    }, [applicationDateSearchTab7, accidentDateSearchTab7, tcIdSearchTab7, activeTab, fetchArbitrationData]);

    // Debouncing effect for Tab 8 search fields - wait 500ms after user stops typing + initial load
    useEffect(() => {
        console.log('🕐 DEBOUNCE TAB 8 USEEFFECT START:', {
            activeTab,
            caseNumberSearch,
            caseNumberLength: caseNumberSearch.trim().length,
            tcIdSearch,
            tcIdLength: tcIdSearch.trim().length,
            accidentDateSearch,
            timestamp: new Date().toISOString(),
            stackTrace: new Error().stack?.split('\n').slice(1, 4)
        });

        if (activeTab === '8') { // Only for Tab 8 (Tahkimdekiler)
            // Check if this is initial load for this tab
            if (isTab8InitialLoad.current && activeTab === '8') {
                console.log('⏸️ DEBOUNCE TAB 8: Initial load, skipping API call');
                isTab8InitialLoad.current = false;
                return;
            }

            // Check if any search field has content OR if fields were just cleared
            const hasAnySearch = caseNumberSearch.trim() || tcIdSearch.trim() || accidentDateSearch.trim();

            console.log('✅ DEBOUNCE TAB 8: Active tab is 8, setting timeout');
            const timeoutId = setTimeout(() => {
                console.log('⏰ DEBOUNCE TAB 8 TIMEOUT EXECUTED:', {
                    caseNumberSearch,
                    caseNumberLength: caseNumberSearch.trim().length,
                    tcIdSearch,
                    tcIdLength: tcIdSearch.trim().length,
                    accidentDateSearch,
                    hasAnySearch,
                    willCallAPI: true
                });

                if (hasAnySearch) {
                    // Esas numarası kontrolü - en az 3 karakter gerekli
                    const hasCaseNumberSearch = caseNumberSearch && caseNumberSearch.trim().length > 0;
                    const hasValidCaseNumberSearch = caseNumberSearch && caseNumberSearch.trim().length >= 3;

                    if (hasCaseNumberSearch && !hasValidCaseNumberSearch) {
                        console.log('⏸️ DEBOUNCE TAB 8 TIMEOUT: Case Number too short, skipping API call');
                        return;
                    }

                    // TC ID kontrolü - en az 3 karakter gerekli
                    const hasTcIdSearch = tcIdSearch && tcIdSearch.trim().length > 0;
                    const hasValidTcIdSearch = tcIdSearch && tcIdSearch.trim().length >= 3;

                    if (hasTcIdSearch && !hasValidTcIdSearch) {
                        console.log('⏸️ DEBOUNCE TAB 8 TIMEOUT: TC ID too short, skipping API call');
                        return;
                    }

                    // Call fetchArbitrationActiveData for search changes
                    console.log('🔄 DEBOUNCE TAB 8: Calling fetchArbitrationActiveData (with search)');
                    fetchArbitrationActiveData();
                } else {
                    // All fields are empty, call NonSearch to get unfiltered data
                    console.log('� DEBOUNCE TAB 8: Calling fetchArbitrationActiveDataNonSearch (fields cleared)');
                    fetchArbitrationActiveDataNonSearch();
                }
            }, 500); // 500ms delay for all searches

            return () => {
                console.log('🚫 DEBOUNCE TAB 8: Timeout cleared');
                clearTimeout(timeoutId);
            };
        } else {
            console.log('❌ DEBOUNCE TAB 8: Active tab is not 8, skipping');
        }
    }, [caseNumberSearch, tcIdSearch, accidentDateSearch, activeTab, fetchArbitrationActiveData]);

    // Debouncing effect for Tab 9 search fields - wait 500ms after user stops typing
    useEffect(() => {
        console.log('🕐 DEBOUNCE TAB 9 USEEFFECT START:', {
            activeTab,
            applicationDateSearchTab9,
            accidentDateSearchTab9,
            tcIdSearchTab9,
            fileCloseCauseSearchTab9,
            tcIdLength: tcIdSearchTab9.trim().length,
            timestamp: new Date().toISOString(),
            stackTrace: new Error().stack?.split('\n').slice(1, 4)
        });

        if (activeTab === '9') { // Only for Tab 9 (Kapalı Dosyalar)
            // Check if this is initial load for this tab
            if (isTab9InitialLoad.current && activeTab === '9') {
                console.log('⏸️ DEBOUNCE TAB 9: Initial load, skipping API call');
                isTab9InitialLoad.current = false;
                return;
            }

            // Check if any search field has content OR if fields were just cleared
            const hasAnySearch = applicationDateSearchTab9.trim() || accidentDateSearchTab9.trim() || tcIdSearchTab9.trim() || fileCloseCauseSearchTab9.trim();

            console.log('✅ DEBOUNCE TAB 9: Active tab is 9, setting timeout');
            const timeoutId = setTimeout(() => {
                console.log('⏰ DEBOUNCE TAB 9 TIMEOUT EXECUTED:', {
                    applicationDateSearchTab9,
                    accidentDateSearchTab9,
                    tcIdSearchTab9,
                    fileCloseCauseSearchTab9,
                    tcIdLength: tcIdSearchTab9.trim().length,
                    hasAnySearch,
                    willCallAPI: true
                });

                if (hasAnySearch) {
                    // TC ID varsa en az 3 karakter olmalı, yoksa API çağrısı yapmayalım
                    const hasTcIdSearch = tcIdSearchTab9 && tcIdSearchTab9.trim().length > 0;
                    const hasValidTcIdSearch = tcIdSearchTab9 && tcIdSearchTab9.trim().length >= 3;

                    // Eğer TC ID girilmişse ama 3 karakterden azsa API çağrısı yapma
                    if (hasTcIdSearch && !hasValidTcIdSearch) {
                        console.log('⏸️ DEBOUNCE TAB 9 TIMEOUT: TC ID too short, skipping API call');
                        return;
                    }

                    // Call fetchClosedFilesData for search changes
                    console.log('🔄 DEBOUNCE TAB 9: Calling fetchClosedFilesData (with search)');
                    fetchClosedFilesData();
                } else {
                    // All fields are empty, call NonSearch to get unfiltered data
                    console.log('� DEBOUNCE TAB 9: Calling fetchClosedFilesDataNonSearch (fields cleared)');
                    fetchClosedFilesDataNonSearch();
                }
            }, 500); // 500ms delay for all searches

            return () => {
                console.log('🚫 DEBOUNCE TAB 9: Timeout cleared');
                clearTimeout(timeoutId);
            };
        } else {
            console.log('❌ DEBOUNCE TAB 9: Active tab is not 9, skipping');
        }
    }, [applicationDateSearchTab9, accidentDateSearchTab9, tcIdSearchTab9, fileCloseCauseSearchTab9, activeTab, fetchClosedFilesData]);

    // Function to fetch task counts for assignment button
    const fetchTaskCounts = useCallback(async () => {
        if (!canRequestTask) return;
        const countApiEndpoint = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/datapool/task-count';
        if (!countApiEndpoint) {
            console.error("Task count API endpoint is not configured.");
            return;
        }
        setIsCountLoading(true);
        try {
            const response = await fetchFormData({ apiEndpoint: countApiEndpoint });
            let taskData = null;
            if (response && response.isSuccess && response.data) {
                taskData = response.data;
            } else if (response && response.hasOwnProperty('maxTaskCount') && response.hasOwnProperty('todayTaskCount')) {
                taskData = response;
            }
            if (taskData) {
                setMaxTaskCount(taskData.maxTaskCount ?? 0);
                setTodayTaskCount(taskData.todayTaskCount ?? 0);
            } else {
                toast.error("Görev sayıları alınamadı veya yanıt formatı geçersiz.");
                console.error("Unexpected or invalid task count response format:", response);
            }
        } catch (error) {
            toast.error("Görev sayıları alınırken bir hata oluştu.");
            console.error("Error fetching task counts:", error);
        } finally {
            setIsCountLoading(false);
        }
    }, [canRequestTask]);

    // Add new function to fetch appraiser task counts
    const fetchAppraiserTaskCounts = useCallback(async () => {
        if (!canRequestTask || (userRoleConfig.taskType !== 'appraiser' && userRoleConfig.taskType !== 'headAppraiser')) return;

        const countApiEndpoint = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/callform/task-count';
        if (!countApiEndpoint) {
            console.error("Appraiser task count API endpoint is not configured.");
            return;
        }
        setIsAppraiserCountLoading(true);
        try {
            const response = await fetchFormData({ apiEndpoint: countApiEndpoint });
            let taskData = null;
            if (response && response.isSuccess && response.data) {
                taskData = response.data;
            } else if (response && response.hasOwnProperty('myTaskCount') && response.hasOwnProperty('totalTaskCount')) {
                taskData = response;
            }
            if (taskData) {
                setAppraiserTaskCount(taskData.myTaskCount ?? 0);
                setPoolTaskCount(taskData.totalTaskCount ?? 0);
            } else {
                toast.error("Görev sayıları alınamadı veya yanıt formatı geçersiz.");
                console.error("Unexpected or invalid task count response format:", response);
            }
        } catch (error) {
            toast.error("Görev sayıları alınırken bir hata oluştu.");
            console.error("Error fetching appraiser task counts:", error);
        } finally {
            setIsAppraiserCountLoading(false);
        }
    }, [canRequestTask, userRoleConfig.taskType]);

    // useEffect to fetch Own Tasks (Tab 2) when it becomes active
    useEffect(() => {
        const ownTasksTabIndex = 1; // Index of "Yeni Eklenenler"
        const ownTasksTabVisible = visibleTabs.some((_category, index) => index === ownTasksTabIndex);
        if (ownTasksTabVisible && activeTab === (ownTasksTabIndex + 1).toString()) {
            fetchOwnTasks();
        }
    }, [activeTab, fetchOwnTasks, visibleTabs]);

    // useEffect to fetch Arbitration Data (Tab 7) when tab becomes active or pagination changes (NOT for search)
    useEffect(() => {
        console.log('📋 TAB 7 USEEFFECT START:', {
            activeTab,
            arbitrationPage,
            arbitrationPageSize,
            timestamp: new Date().toISOString(),
            stackTrace: new Error().stack?.split('\n').slice(1, 4)
        });

        const arbitrationTabTitle = 'Tahkime Hazırlar';
        const arbitrationTabVisible = visibleTabs.some(tab => tab.title === arbitrationTabTitle);
        const arbitrationTabIndex = TodoCategoriesList.findIndex(tab => tab.title === arbitrationTabTitle);
        const arbitrationTabId = (arbitrationTabIndex + 1).toString(); // Tab ID is index + 1

        console.log('📋 TAB 7 USEEFFECT conditions:', {
            arbitrationTabVisible,
            activeTabMatches: activeTab === arbitrationTabId,
            arbitrationTabId,
            visibleTabs: visibleTabs.map(t => t.title),
            note: 'This useEffect should NOT be triggered by search field changes'
        });

        if (arbitrationTabVisible && activeTab === arbitrationTabId) {
            console.log('✅ TAB 7 USEEFFECT: Conditions met, calling fetchArbitrationDataNonSearch (pagination/tab change)');
            fetchArbitrationDataNonSearch();
        } else {
            console.log('❌ TAB 7 USEEFFECT: Conditions not met');
        }
    }, [activeTab, arbitrationPage, arbitrationPageSize, visibleTabs, fetchArbitrationDataNonSearch]);

    // useEffect to fetch Arbitration Active Data (Tab 8) when tab becomes active or pagination/filter changes (NOT for search)
    useEffect(() => {
        console.log('📋 TAB 8 USEEFFECT START:', {
            activeTab,
            arbitrationActivePage,
            arbitrationActivePageSize,
            selectedArbitrationStateFilter,
            timestamp: new Date().toISOString(),
            stackTrace: new Error().stack?.split('\n').slice(1, 4)
        });

        const arbitrationActiveTabTitle = 'Tahkimdekiler';
        const arbitrationActiveTabVisible = visibleTabs.some(tab => tab.title === arbitrationActiveTabTitle);
        const arbitrationActiveTabIndex = TodoCategoriesList.findIndex(tab => tab.title === arbitrationActiveTabTitle);
        const arbitrationActiveTabId = (arbitrationActiveTabIndex + 1).toString(); // Tab ID is index + 1

        console.log('📋 TAB 8 USEEFFECT conditions:', {
            arbitrationActiveTabVisible,
            activeTabMatches: activeTab === arbitrationActiveTabId,
            arbitrationActiveTabId,
            visibleTabs: visibleTabs.map(t => t.title),
            note: 'This useEffect should NOT be triggered by caseNumberSearch changes'
        });

        if (arbitrationActiveTabVisible && activeTab === arbitrationActiveTabId) {
            console.log('✅ TAB 8 USEEFFECT: Conditions met, calling fetchArbitrationActiveDataNonSearch (pagination/filter change)');
            fetchArbitrationActiveDataNonSearch();
        } else {
            console.log('❌ TAB 8 USEEFFECT: Conditions not met');
        }
    }, [activeTab, arbitrationActivePage, arbitrationActivePageSize, selectedArbitrationStateFilter, visibleTabs, fetchArbitrationActiveDataNonSearch]);

    // useEffect to fetch Closed Files Data (Tab 9) when tab becomes active or pagination changes (NOT for search)
    useEffect(() => {
        console.log('📋 TAB 9 USEEFFECT START:', {
            activeTab,
            closedFilesPage,
            closedFilesPageSize,
            timestamp: new Date().toISOString(),
            stackTrace: new Error().stack?.split('\n').slice(1, 4)
        });

        const closedFilesTabTitle = 'Kapalı Dosyalar';
        const closedFilesTabVisible = visibleTabs.some(tab => tab.title === closedFilesTabTitle);
        const closedFilesTabIndex = TodoCategoriesList.findIndex(tab => tab.title === closedFilesTabTitle);
        const closedFilesTabId = (closedFilesTabIndex + 1).toString(); // Tab ID is index + 1

        console.log('📋 TAB 9 USEEFFECT conditions:', {
            closedFilesTabVisible,
            activeTabMatches: activeTab === closedFilesTabId,
            closedFilesTabId,
            visibleTabs: visibleTabs.map(t => t.title),
            note: 'This useEffect should NOT be triggered by search field changes'
        });

        if (closedFilesTabVisible && activeTab === closedFilesTabId) {
            console.log('✅ TAB 9 USEEFFECT: Conditions met, calling fetchClosedFilesDataNonSearch (pagination/tab change)');
            fetchClosedFilesDataNonSearch();
        } else {
            console.log('❌ TAB 9 USEEFFECT: Conditions not met');
        }
    }, [activeTab, closedFilesPage, closedFilesPageSize, visibleTabs, fetchClosedFilesDataNonSearch]);

    // Update useEffect to fetch appropriate counts based on role
    useEffect(() => {
        if (canRequestTask) {
            if (userRoleConfig.taskType === 'appraiser' || userRoleConfig.taskType === 'headAppraiser') {
                fetchAppraiserTaskCounts();
            } else {
                fetchTaskCounts();
            }
        }
    }, [fetchTaskCounts, fetchAppraiserTaskCounts, canRequestTask, userRoleConfig.taskType]);

    // Handler for "Yeni İş Talep Et" button
    const handleRequestNewJobClick = useCallback(async () => {
        if (isAssigningTask) return;

        // Appraiser ve Head Appraiser için ortak endpoint
        if (userRoleConfig.taskType === 'appraiser' || userRoleConfig.taskType === 'headAppraiser') {
            const assignApiEndpoint = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/callform/assign-task';
            if (!assignApiEndpoint) {
                toast.error("Assign task API endpoint is not configured.");
                return;
            }
            setIsAssigningTask(true);
            try {
                const responseData = await requestNewTask({ apiEndpoint: assignApiEndpoint });
                if (responseData && responseData.isSuccess !== false) {
                    toast.success(responseData.message || "Yeni iş başarıyla atandı!");
                    await fetchAppraiserTaskCounts();
                    await fetchOwnTasks();
                    setListRefreshKey(prevKey => prevKey + 1);
                } else {
                    const defaultErrorMessage = "İş atama sırasında bilinmeyen bir hata oluştu.";
                    const specificMessage = "Atanabilecek görev bulunamadı.";
                    if (responseData && responseData.isSuccess === false && responseData.message === specificMessage) {
                        toast.warn(specificMessage);
                    } else {
                        toast.error(responseData?.message || defaultErrorMessage);
                    }
                    console.error("Assign task API failure or error (handled in component):", responseData);
                }
            } catch (error) {
                console.error("Error during handleRequestNewJobClick:", error);
            } finally {
                setIsAssigningTask(false);
            }
            return;
        }

        // Call Center ve Head Call Center için ortak endpoint
        if (userRoleConfig.taskType === 'callCenter' || userRoleConfig.taskType === 'headCallCenter') {
            if (todayTaskCount >= maxTaskCount && maxTaskCount > 0) {
                toast.warn("Günlük maksimum iş talep sayısına ulaştınız.");
                return;
            }
            const assignApiEndpoint = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/datapool/assign-task';
            if (!assignApiEndpoint) {
                toast.error("Assign task API endpoint is not configured.");
                return;
            }
            setIsAssigningTask(true);
            try {
                const responseData = await requestNewTask({ apiEndpoint: assignApiEndpoint });
                if (responseData && responseData.isSuccess !== false) {
                    toast.success(responseData.message || "Yeni iş başarıyla atandı!");
                    await fetchTaskCounts();
                    await fetchOwnTasks();
                    setListRefreshKey(prevKey => prevKey + 1);
                } else {
                    const defaultErrorMessage = "İş atama sırasında bilinmeyen bir hata oluştu.";
                    const specificMessage = "Atanabilecek görev bulunamadı.";
                    if (responseData && responseData.isSuccess === false && responseData.message === specificMessage) {
                        toast.warn(specificMessage);
                    } else {
                        toast.error(responseData?.message || defaultErrorMessage);
                    }
                    console.error("Assign task API failure or error (handled in component):", responseData);
                }
            } catch (error) {
                console.error("Error during handleRequestNewJobClick:", error);
            } finally {
                setIsAssigningTask(false);
            }
        }
    }, [isAssigningTask, todayTaskCount, maxTaskCount, fetchTaskCounts, fetchOwnTasks, fetchAppraiserTaskCounts, userRoleConfig.taskType]);

    // Pagination Handlers for Tab 2
    const handleOwnTasksPageChange = (newPage) => setOwnTasksPage(newPage);
    const handleOwnTasksPerPageChange = (newPerPage, newPage) => {
        setOwnTasksPageSize(newPerPage);
        setOwnTasksPage(newPage);
    };

    // Pagination Handlers for Tab 7 (Arbitration)
    const handleArbitrationPageChange = (newPage) => setArbitrationPage(newPage);
    const handleArbitrationPerPageChange = (newPerPage, newPage) => {
        setArbitrationPageSize(newPerPage);
        setArbitrationPage(newPage);
    };

    // Pagination Handlers for Tab 8 (Arbitration Active)
    const handleArbitrationActivePageChange = (newPage) => setArbitrationActivePage(newPage);
    const handleArbitrationActivePerPageChange = (newPerPage, newPage) => {
        setArbitrationActivePageSize(newPerPage);
        setArbitrationActivePage(newPage);
    };

    // Pagination Handlers for Tab 9 (Closed Files)
    const handleClosedFilesPageChange = (newPage) => setClosedFilesPage(newPage);
    const handleClosedFilesPerPageChange = (newPerPage, newPage) => {
        setClosedFilesPageSize(newPerPage);
        setClosedFilesPage(newPage);
    };

    // Search Handlers for Tab 7 (Tahkime Hazırlar)
    const handleApplicationDateSearchTab7Change = (event) => {
        const dateValue = event.target.value; // YYYY-MM-DD formatında gelir

        console.log('🔍 APPLICATION DATE SEARCH HANDLER (Tab 7) called:', {
            oldValue: applicationDateSearchTab7,
            dateValue: dateValue,
            timestamp: new Date().toISOString()
        });

        setApplicationDateSearchTab7(dateValue);
        setArbitrationPage(1); // Reset to first page when searching
    };

    const handleAccidentDateSearchTab7Change = (event) => {
        const dateValue = event.target.value; // YYYY-MM-DD formatında gelir

        console.log('🔍 ACCIDENT DATE SEARCH HANDLER (Tab 7) called:', {
            oldValue: accidentDateSearchTab7,
            dateValue: dateValue,
            timestamp: new Date().toISOString()
        });

        setAccidentDateSearchTab7(dateValue);
        setArbitrationPage(1); // Reset to first page when searching
    };

    const handleTcIdSearchTab7Change = (event) => {
        const inputValue = event.target.value;
        // Sadece rakam girişine izin ver
        const numericValue = inputValue.replace(/[^0-9]/g, '');

        console.log('🔍 TC ID SEARCH HANDLER (Tab 7) called:', {
            oldValue: tcIdSearchTab7,
            inputValue: inputValue,
            numericValue: numericValue,
            timestamp: new Date().toISOString()
        });

        setTcIdSearchTab7(numericValue);
        setArbitrationPage(1); // Reset to first page when searching
    };

    // Normalize Turkish characters to English equivalents
    const normalizeToEnglish = (text) => {
        const turkishToEnglish = {
            'Ç': 'C', 'ç': 'c',
            'Ğ': 'G', 'ğ': 'g',
            'İ': 'I', 'ı': 'i',
            'Ö': 'O', 'ö': 'o',
            'Ş': 'S', 'ş': 's',
            'Ü': 'U', 'ü': 'u'
        };

        return text.replace(/[ÇçĞğİıÖöŞşÜü]/g, (match) => turkishToEnglish[match] || match);
    };

    // Search Handler for Tab 8 (Arbitration Active - Case Number)
    const handleCaseNumberSearchChange = (event) => {
        const inputValue = event.target.value;
        const upperCaseValue = inputValue.toUpperCase(); // Önce büyük harfe çevir
        const normalizedValue = normalizeToEnglish(upperCaseValue); // Sonra Türkçe karakterleri normalize et

        console.log('🔍 CASE NUMBER SEARCH HANDLER called:', {
            oldValue: caseNumberSearch,
            inputValue: inputValue,
            upperCaseValue: upperCaseValue,
            normalizedValue: normalizedValue,
            timestamp: new Date().toISOString()
        });

        setCaseNumberSearch(normalizedValue);
        setArbitrationActivePage(1); // Reset to first page when searching

        // Esas numarası girildiğinde diğer filtreleri sıfırla
        if (normalizedValue && normalizedValue.trim().length >= 3) {
            console.log('🔄 CASE NUMBER SEARCH HANDLER: Clearing other filters');
            setTcIdSearch('');
            setAccidentDateSearch('');
            setSelectedArbitrationStateFilter('');
        }
    };

    // Search Handler for Tab 8 (Arbitration Active - TC ID)
    const handleTcIdSearchChange = (event) => {
        const inputValue = event.target.value;
        // Sadece rakam girişine izin ver
        const numericValue = inputValue.replace(/[^0-9]/g, '');

        console.log('🔍 TC ID SEARCH HANDLER called:', {
            oldValue: tcIdSearch,
            inputValue: inputValue,
            numericValue: numericValue,
            timestamp: new Date().toISOString()
        });

        setTcIdSearch(numericValue);
        setArbitrationActivePage(1); // Reset to first page when searching

        // TC ID girildiğinde diğer filtreleri sıfırla (kaza tarihi hariç)
        if (numericValue && numericValue.trim().length > 0) {
            console.log('🔄 TC ID SEARCH HANDLER: Clearing other filters (except accident date)');
            setCaseNumberSearch('');
            setSelectedArbitrationStateFilter('');
        }
    };

    // Search Handler for Tab 8 (Arbitration Active - Accident Date)
    const handleAccidentDateSearchChange = (event) => {
        const dateValue = event.target.value; // YYYY-MM-DD formatında gelir

        console.log('🔍 ACCIDENT DATE SEARCH HANDLER called:', {
            oldValue: accidentDateSearch,
            dateValue: dateValue,
            timestamp: new Date().toISOString()
        });

        setAccidentDateSearch(dateValue);
        setArbitrationActivePage(1); // Reset to first page when searching

        // Kaza tarihi girildiğinde diğer filtreleri sıfırla (TC ID hariç)
        if (dateValue && dateValue.trim().length > 0) {
            console.log('🔄 ACCIDENT DATE SEARCH HANDLER: Clearing other filters (except TC ID)');
            setCaseNumberSearch('');
            setSelectedArbitrationStateFilter('');
        }
    };

    // Search Handlers for Tab 9 (Kapalı Dosyalar)
    const handleApplicationDateSearchTab9Change = (event) => {
        const dateValue = event.target.value; // YYYY-MM-DD formatında gelir

        console.log('🔍 APPLICATION DATE SEARCH HANDLER (Tab 9) called:', {
            oldValue: applicationDateSearchTab9,
            dateValue: dateValue,
            timestamp: new Date().toISOString()
        });

        setApplicationDateSearchTab9(dateValue);
        setClosedFilesPage(1); // Reset to first page when searching
    };

    const handleAccidentDateSearchTab9Change = (event) => {
        const dateValue = event.target.value; // YYYY-MM-DD formatında gelir

        console.log('🔍 ACCIDENT DATE SEARCH HANDLER (Tab 9) called:', {
            oldValue: accidentDateSearchTab9,
            dateValue: dateValue,
            timestamp: new Date().toISOString()
        });

        setAccidentDateSearchTab9(dateValue);
        setClosedFilesPage(1); // Reset to first page when searching
    };

    const handleTcIdSearchTab9Change = (event) => {
        const inputValue = event.target.value;
        // Sadece rakam girişine izin ver
        const numericValue = inputValue.replace(/[^0-9]/g, '');

        console.log('🔍 TC ID SEARCH HANDLER (Tab 9) called:', {
            oldValue: tcIdSearchTab9,
            inputValue: inputValue,
            numericValue: numericValue,
            timestamp: new Date().toISOString()
        });

        setTcIdSearchTab9(numericValue);
        setClosedFilesPage(1); // Reset to first page when searching
    };

    const handleFileCloseCauseSearchTab9Change = (event) => {
        const selectedValue = event.target.value;

        console.log('� FILE CLOSE CAUSE COMBOBOX HANDLER (Tab 9) called:', {
            oldValue: fileCloseCauseSearchTab9,
            selectedValue: selectedValue,
            timestamp: new Date().toISOString()
        });

        setFileCloseCauseSearchTab9(selectedValue);
        setClosedFilesPage(1); // Reset to first page when searching
    };

    // Filter Handler for Tab 8 (Arbitration Active)
    const handleArbitrationStateFilterChange = (event) => {
        console.log('🔍 ARBITRATION STATE FILTER HANDLER called:', {
            oldValue: selectedArbitrationStateFilter,
            newValue: event.target.value,
            timestamp: new Date().toISOString()
        });

        setSelectedArbitrationStateFilter(event.target.value);
        setArbitrationActivePage(1); // Reset to first page when filtering
    };

    // Modal handlers for Dosya Kapa
    const openCloseFileModal = (fileId) => {
        setCurrentCloseFileId(fileId);
        setFileStatus('Kapalı');
        setFileCloseDescription('');
        setFileCloseComment('');
        setIsCloseFileModalOpen(true);
    };

    const closeFileModal = () => {
        setIsCloseFileModalOpen(false);
        setCurrentCloseFileId(null);
        setFileStatus('Kapalı');
        setFileCloseDescription('');
        setFileCloseComment('');
    };

    const handleCloseFile = async () => {
        if (!currentCloseFileId) return;

        if (!fileCloseDescription.trim()) {
            toast.error('Lütfen kapatma açıklaması giriniz.');
            return;
        }

        if (!fileCloseComment.trim()) {
            toast.error('Lütfen dosya kapatma yorumu giriniz.');
            return;
        }

        setIsClosingFile(true);

        try {
            const responseData = await closeAccidentFile({
                accidentFileId: currentCloseFileId,
                fileStatus: fileStatus,
                fileCloseDescription: fileCloseDescription.trim(),
                fileCloseComment: fileCloseComment.trim()
            });

            toast.success('Dosya durumu başarıyla güncellendi!');
            fetchArbitrationData(); // Refresh the data
            closeFileModal();

        } catch (error) {
            console.error('Error closing file:', error);
            // Error toast is already handled by the API function
        } finally {
            setIsClosingFile(false);
        }
    };

    // Modal handlers for Başvuru Ücreti
    const openApplicationFeeModal = (fileId) => {
        setCurrentApplicationFileId(fileId);
        setArbitrationApplicationFee('');
        setIsApplicationFeeModalOpen(true);
    };

    const closeApplicationFeeModal = () => {
        setIsApplicationFeeModalOpen(false);
        setCurrentApplicationFileId(null);
        setArbitrationApplicationFee('');
    };

    const handleSubmitApplicationFee = async () => {
        if (!currentApplicationFileId) return;

        if (!arbitrationApplicationFee.trim()) {
            toast.error('Lütfen başvuru ücretini giriniz.');
            return;
        }

        // String'i integer'a çevir
        const feeAmount = parseInt(arbitrationApplicationFee.trim());
        if (isNaN(feeAmount) || feeAmount < 0) {
            toast.error('Lütfen geçerli bir ücret miktarı giriniz.');
            return;
        }

        setIsSubmittingApplicationFee(true);
        const apiEndpoint = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/Arbitration';

        const requestBody = {
            arbitrationDto: {
                appointedUser: [],
                arbitrationState: [],
                accidentFile: [currentApplicationFileId.toString()],
                arbitrationInformation: []
            },
            arbitrationInformationDto: {
                arbitration: [], // Tamamen boş array
                applicationDate: new Date().toISOString(),
                caseNumber: "",
                priceForNow: 0,
                arbitrationApplicationFee: feeAmount,
                expenses: [] // Tamamen boş array
            }
        };

        try {
            const response = await fetch(apiEndpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody)
            });

            const responseData = await response.json();

            if (response.ok && responseData.isSuccess !== false) {
                toast.success('Başvuru ücreti başarıyla kaydedildi!');
                fetchArbitrationData(); // Refresh the data
                closeApplicationFeeModal();
            } else {
                toast.error(responseData?.message || 'Başvuru ücreti kaydedilirken bir hata oluştu.');
            }
        } catch (error) {
            console.error('Error submitting application fee:', error);
            toast.error('Başvuru ücreti kaydedilirken bir hata oluştu.');
        } finally {
            setIsSubmittingApplicationFee(false);
        }
    };

    // Arbitration Expense Modal Handlers (Tab 8)
    const handleArbitrationExpenseModal = (arbitrationItem) => {
        console.log('Opening arbitration expense modal for:', arbitrationItem);
        setCurrentArbitrationItem(arbitrationItem);
        setIsArbitrationExpenseModalOpen(true);
    };

    const closeArbitrationExpenseModal = () => {
        setIsArbitrationExpenseModalOpen(false);
        setCurrentArbitrationItem(null);
    };

    const refreshArbitrationActiveData = () => {
        console.log('Refreshing arbitration active data...');
        fetchArbitrationActiveData();
    };

    // FileCloseModal Handlers (Tab 8)
    const handleFileCloseModal = (arbitrationItem) => {
        console.log('Opening file close modal for:', arbitrationItem);
        setCurrentArbitrationIdForClose(arbitrationItem.id);
        setIsFileCloseModalOpen(true);
    };

    const closeFileCloseModal = () => {
        setIsFileCloseModalOpen(false);
        setCurrentArbitrationIdForClose(null);
    };

    const refreshArbitrationDataAfterClose = () => {
        // Dosya kapatıldıktan sonra listeyi yenile
        refreshArbitrationActiveData();
    };

    // Arbitration Information Modal Handlers (Tab 8)
    const openArbitrationInfoModal = (arbitrationItem) => {
        console.log('Opening arbitration info modal for:', arbitrationItem);
        setCurrentArbitrationInfoItem(arbitrationItem);

        // Pre-fill form with existing data if available
        const arbInfo = arbitrationItem.arbitrationInformation;
        if (arbInfo) {
            // datetime-local format için ISO string'i YYYY-MM-DDTHH:MM formatına çevir
            if (arbInfo.applicationDate) {
                const date = new Date(arbInfo.applicationDate);
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                const hours = String(date.getHours()).padStart(2, '0');
                const minutes = String(date.getMinutes()).padStart(2, '0');
                setArbitrationApplicationDate(`${year}-${month}-${day}T${hours}:${minutes}`);
            } else {
                setArbitrationApplicationDate('');
            }
            setArbitrationCaseNumber(arbInfo.caseNumber || '');
            setArbitrationPriceForNow(arbInfo.priceForNow || '');
        } else {
            // Clear form for new data
            setArbitrationApplicationDate('');
            setArbitrationCaseNumber('');
            setArbitrationPriceForNow('');
        }

        setIsArbitrationInfoModalOpen(true);
    };

    const closeArbitrationInfoModal = () => {
        setIsArbitrationInfoModalOpen(false);
        setCurrentArbitrationInfoItem(null);
        setArbitrationApplicationDate('');
        setArbitrationCaseNumber('');
        setArbitrationPriceForNow('');
    };

    const handleArbitrationEdit = (arbitrationItem) => {
        // Navigate to arbitration edit page
        console.log('Navigate to edit arbitration page:', arbitrationItem);
        navigate(`${process.env.PUBLIC_URL}/arbitration/${arbitrationItem.id}`);
    };

    const handleArbitrationAssignToSelf = async (arbitrationId) => {
        if (isAssigningTask) return;

        setIsAssigningTask(true);
        try {
            const responseData = await assignArbitrationToSelf({ arbitrationId });

            toast.success(responseData?.message || "Tahkim işi başarıyla kendinize atandı!");
            fetchArbitrationActiveData(); // Refresh data

        } catch (error) {
            console.error("Error during handleArbitrationAssignToSelf:", error);
            // Error toast is already handled by the API interceptor for most cases
            // Only show generic toast if it's not a server error
            if (!error.response || error.response.status >= 500) {
                toast.error("Tahkim işi kendinize atanırken bir hata oluştu.");
            }
        } finally {
            setIsAssigningTask(false);
        }
    };

    const handleArbitrationReleaseFromSelf = async (arbitrationId) => {
        if (isReleasingTask) return;

        setIsReleasingTask(true);
        try {
            const responseData = await releaseArbitrationFromSelf({ arbitrationId });

            toast.success(responseData?.message || "Tahkim işi başarıyla kendinizden kaldırıldı!");
            fetchArbitrationActiveData(); // Refresh data

        } catch (error) {
            console.error("Error during handleArbitrationReleaseFromSelf:", error);
            // Error toast is already handled by the API interceptor for most cases
            // Only show generic toast if it's not a server error
            if (!error.response || error.response.status >= 500) {
                toast.error("Tahkim işi kendinizden kaldırılırken bir hata oluştu.");
            }
        } finally {
            setIsReleasingTask(false);
        }
    };

    const handleSubmitArbitrationInfo = async () => {
        if (!currentArbitrationInfoItem?.arbitrationInformation?.id) {
            toast.error('Tahkim bilgi ID bulunamadı!');
            return;
        }

        if (!arbitrationApplicationDate || !arbitrationCaseNumber) {
            toast.error('Başvuru tarihi ve dava numarası zorunludur!');
            return;
        }

        setIsSubmittingArbitrationInfo(true);

        const payload = {
            applicationDate: new Date(arbitrationApplicationDate).toISOString(),
            caseNumber: arbitrationCaseNumber,
            priceForNow: parseFloat(arbitrationPriceForNow) || 0
        };

        try {
            const apiEndpoint = `${process.env.REACT_APP_LACIVERT_API_ENDPOINT}/Arbitration/patch-arbitration-information/${currentArbitrationInfoItem.arbitrationInformation.id}`;

            const response = await fetch(apiEndpoint, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                toast.success('Tahkim bilgileri başarıyla güncellendi!');
                closeArbitrationInfoModal();
                fetchArbitrationActiveData(); // Refresh data
            } else {
                const errorData = await response.text();
                console.error('API Error:', errorData);
                toast.error('Tahkim bilgileri güncellenirken hata oluştu!');
            }
        } catch (error) {
            console.error('Submit arbitration info error:', error);
            toast.error('Tahkim bilgileri güncellenirken hata oluştu!');
        } finally {
            setIsSubmittingArbitrationInfo(false);
        }
    };

    // Renders content for Tab 2 (Own Tasks)
    const renderOwnTasksContent = () => (
        <DataTable
            columns={dataPoolColumns} data={ownTasksItems} progressPending={isOwnTasksLoading}
            pagination paginationServer paginationTotalRows={ownTasksTotalRows}
            onChangePage={handleOwnTasksPageChange} onChangeRowsPerPage={handleOwnTasksPerPageChange}
            paginationPerPage={ownTasksPageSize} paginationDefaultPage={ownTasksPage}
            striped highlightOnHover responsive
        />
    );

    // Renders content for Tab 7 (Tahkime Hazırlar)
    const renderArbitrationContent = () => (
        <Container fluid>
            <Row>
                <Col sm="12">
                    <Row>
                        {/* Başvuru Tarihi */}
                        <Col md="4">
                            <FormGroup>
                                <Label>Başvuru Tarihi</Label>
                                <Input
                                    type="date"
                                    value={applicationDateSearchTab7}
                                    onChange={handleApplicationDateSearchTab7Change}
                                    placeholder="Başvuru tarihi seçin"
                                />
                            </FormGroup>
                        </Col>

                        {/* Plaka Kaza Tarihi */}
                        <Col md="4">
                            <FormGroup>
                                <Label>Plaka Kaza Tarihi</Label>
                                <Input
                                    type="date"
                                    value={accidentDateSearchTab7}
                                    onChange={handleAccidentDateSearchTab7Change}
                                    placeholder="Kaza tarihi seçin"
                                />
                            </FormGroup>
                        </Col>

                        {/* Müşteri TC ID */}
                        <Col md="4">
                            <FormGroup>
                                <Label>TC Kimlik Numarası / Vergi Numarası ile Ara</Label>
                                <Input
                                    type="text"
                                    placeholder="TC kimlik numarası / Vergi numarası..."
                                    value={tcIdSearchTab7}
                                    onChange={handleTcIdSearchTab7Change}
                                    maxLength="11"
                                />
                                {tcIdSearchTab7 && tcIdSearchTab7.trim().length > 0 && tcIdSearchTab7.trim().length < 3 && (
                                    <small className="text-muted">En az 3 karakter girmelisiniz</small>
                                )}
                            </FormGroup>
                        </Col>
                    </Row>
                    <DataTable
                        columns={accidentFileColumns}
                        data={arbitrationItems}
                        progressPending={isArbitrationLoading}
                        pagination
                        paginationServer
                        paginationTotalRows={arbitrationTotalRows}
                        onChangePage={handleArbitrationPageChange}
                        onChangeRowsPerPage={handleArbitrationPerPageChange}
                        paginationPerPage={arbitrationPageSize}
                        paginationDefaultPage={arbitrationPage}
                        striped
                        highlightOnHover
                        responsive
                        noDataComponent="Tahkime hazır kaza dosyası bulunamadı"
                    />
                </Col>
            </Row>
        </Container>
    );

    // Renders content for Tab 8 (Tahkimdekiler)
    const renderArbitrationActiveContent = () => {
        // Disable conditions
        const hasCaseNumberSearch = caseNumberSearch && caseNumberSearch.trim().length >= 3;
        const hasTcIdSearch = tcIdSearch && tcIdSearch.trim().length > 0;
        const hasAccidentDateSearch = accidentDateSearch && accidentDateSearch.trim().length > 0;
        const hasOtherSearches = hasTcIdSearch || hasAccidentDateSearch;

        return (
            <Container fluid>
                <Row>
                    <Col sm="12">
                        <Row>
                            {/* Esas Numarası ile Ara */}
                            <Col md="3">
                                <FormGroup>
                                    <Label>Esas Numarası ile Ara</Label>
                                    <Input
                                        type="text"
                                        placeholder="Esas numarası ile ara... (en az 3 karakter)"
                                        value={caseNumberSearch}
                                        onChange={handleCaseNumberSearchChange}
                                        style={{ textTransform: 'uppercase' }}
                                        disabled={hasOtherSearches}
                                    />
                                    {caseNumberSearch && caseNumberSearch.trim().length > 0 && caseNumberSearch.trim().length < 3 && (
                                        <small className="text-muted">En az 3 karakter girmelisiniz</small>
                                    )}
                                    {hasOtherSearches && (
                                        <small className="text-muted">Diğer aramalar aktifken kullanılamaz</small>
                                    )}
                                </FormGroup>
                            </Col>

                            {/* TC Kimlik Numarası / Veri Numarası ile Ara */}
                            <Col md="3">
                                <FormGroup>
                                    <Label>TC Kimlik Numarası / Vergi Numarası ile Ara</Label>
                                    <Input
                                        type="text"
                                        placeholder="TC kimlik numarası / Vergi numarası..."
                                        value={tcIdSearch}
                                        onChange={handleTcIdSearchChange}
                                        disabled={hasCaseNumberSearch}
                                        maxLength="11"
                                    />
                                    {tcIdSearch && tcIdSearch.trim().length > 0 && tcIdSearch.trim().length < 3 && (
                                        <small className="text-muted">En az 3 karakter girmelisiniz</small>
                                    )}
                                    {hasCaseNumberSearch && (
                                        <small className="text-muted">Esas numarası araması aktifken kullanılamaz</small>
                                    )}
                                </FormGroup>
                            </Col>

                            {/* Plaka Kaza Tarihi */}
                            <Col md="3">
                                <FormGroup>
                                    <Label>Plaka Kaza Tarihi</Label>
                                    <Input
                                        type="date"
                                        value={accidentDateSearch}
                                        onChange={handleAccidentDateSearchChange}
                                        disabled={hasCaseNumberSearch}
                                    />
                                    {hasCaseNumberSearch && (
                                        <small className="text-muted">Esas numarası araması aktifken kullanılamaz</small>
                                    )}
                                </FormGroup>
                            </Col>

                            {/* Tahkim Durumu Filtrele */}
                            <Col md="3">
                                <FormGroup>
                                    <Label>Tahkim Durumu Filtrele</Label>
                                    <Input
                                        type="select"
                                        value={selectedArbitrationStateFilter}
                                        onChange={handleArbitrationStateFilterChange}
                                        disabled={hasCaseNumberSearch || hasOtherSearches}
                                    >
                                        <option value="">Tümü</option>
                                        {arbitrationStatesList.map(state => (
                                            <option key={state.id} value={state.id}>
                                                {state.title}
                                            </option>
                                        ))}
                                    </Input>
                                    {(hasCaseNumberSearch || hasOtherSearches) && (
                                        <small className="text-muted">Diğer aramalar aktifken kullanılamaz</small>
                                    )}
                                </FormGroup>
                            </Col>
                        </Row>
                        <DataTable
                            columns={arbitrationActiveColumns}
                            data={arbitrationActiveItems}
                            progressPending={isArbitrationActiveLoading}
                            pagination
                            paginationServer
                            paginationTotalRows={arbitrationActiveTotalRows}
                            onChangePage={handleArbitrationActivePageChange}
                            onChangeRowsPerPage={handleArbitrationActivePerPageChange}
                            paginationPerPage={arbitrationActivePageSize}
                            paginationDefaultPage={arbitrationActivePage}
                            striped
                            highlightOnHover
                            responsive
                            noDataComponent="Tahkimde bulunan dosya bulunamadı"
                        />
                    </Col>
                </Row>
            </Container>
        );
    };

    // Renders content for Tab 9 (Kapalı Dosyalar)
    const renderClosedFilesContent = () => (
        <Container fluid>
            <Row>
                <Col sm="12">
                    <Row className="mb-3">
                        <Col md="3">
                            <FormGroup>
                                <Label>Başvuru Tarihi</Label>
                                <Input
                                    type="date"
                                    value={applicationDateSearchTab9}
                                    onChange={handleApplicationDateSearchTab9Change}
                                    placeholder="Başvuru tarihi seçin..."
                                />
                            </FormGroup>
                        </Col>
                        <Col md="3">
                            <FormGroup>
                                <Label>Plaka Kaza Tarihi</Label>
                                <Input
                                    type="date"
                                    value={accidentDateSearchTab9}
                                    onChange={handleAccidentDateSearchTab9Change}
                                    placeholder="Kaza tarihi seçin..."
                                />
                            </FormGroup>
                        </Col>
                        <Col md="3">
                            <FormGroup>
                                <Label>TC Kimlik Numarası / Vergi Numarası ile Ara</Label>
                                <Input
                                    type="text"
                                    placeholder="TC kimlik numarası / Vergi numarası..."
                                    value={tcIdSearchTab9}
                                    onChange={handleTcIdSearchTab9Change}
                                    maxLength="11"
                                />
                                {tcIdSearchTab9 && tcIdSearchTab9.trim().length > 0 && tcIdSearchTab9.trim().length < 3 && (
                                    <small className="text-muted">En az 3 karakter girmelisiniz</small>
                                )}
                            </FormGroup>
                        </Col>
                        <Col md="3">
                            <FormGroup>
                                <Label>Kapanma Açıklaması ile Ara</Label>
                                <Input
                                    type="select"
                                    value={fileCloseCauseSearchTab9}
                                    onChange={handleFileCloseCauseSearchTab9Change}
                                >
                                    <option value="">Kapanma açıklaması seçin...</option>
                                    <option value="Gelen ödeme yeterli">Gelen ödeme yeterli</option>
                                    <option value="Aynı yerden eski hasarlı">Aynı yerden eski hasarlı</option>
                                    <option value="Ağır hasarlı">Ağır hasarlı</option>
                                    <option value="Farklı vekil ödeme almış">Farklı vekil ödeme almış</option>
                                    <option value="Farklı vekil tahkime başvuru yapmış">Farklı vekil tahkime başvuru yapmış</option>
                                    <option value="Vekalet süresi bitti">Vekalet süresi bitti</option>
                                    <option value="Azil">Azil</option>
                                    <option value="Teminat limiti dolu">Teminat limiti dolu</option>
                                    <option value="Sulh">Sulh</option>
                                    <option value="Geri çekme">Geri çekme</option>
                                    <option value="Feragat">Feragat</option>
                                    <option value="Bilirkişi ücreti yatırılmadı">Bilirkişi ücreti yatırılmadı</option>
                                    <option value="Kusur değişmedi">Kusur değişmedi</option>
                                    <option value="Bakiye limit çıkmadı">Bakiye limit çıkmadı</option>
                                    <option value="Görevsizlik">Görevsizlik</option>
                                    <option value="Kısmi dava açıldığından">Kısmi dava açıldığından</option>
                                    <option value="Fatura olmadığından">Fatura olmadığından</option>
                                    <option value="Derdestlik">Derdestlik</option>
                                    <option value="Zaman aşımı">Zaman aşımı</option>
                                    <option value="Yanlış başvuru">Yanlış başvuru</option>
                                    <option value="Diğer">Diğer</option>
                                </Input>
                            </FormGroup>
                        </Col>
                    </Row>
                    <DataTable
                        columns={closedFilesColumns}
                        data={closedFilesItems}
                        progressPending={isClosedFilesLoading}
                        pagination
                        paginationServer
                        paginationTotalRows={closedFilesTotalRows}
                        onChangePage={handleClosedFilesPageChange}
                        onChangeRowsPerPage={handleClosedFilesPerPageChange}
                        paginationPerPage={closedFilesPageSize}
                        paginationDefaultPage={closedFilesPage}
                        striped
                        highlightOnHover
                        responsive
                        noDataComponent="Kapalı dosya bulunamadı"
                    />
                </Col>
            </Row>
        </Container>
    );

    // Buton disabled durumu için
    const assignButtonDisabled = ['appraiser', 'headAppraiser'].includes(userRoleConfig.taskType)
        ? isAssigningTask
        : (isAssigningTask || (todayTaskCount >= maxTaskCount && maxTaskCount > 0));

    // Buton başlığı için
    const assignButtonTitle = ['appraiser', 'headAppraiser'].includes(userRoleConfig.taskType)
        ? (isAssigningTask ? "İş atanıyor..." : "Yeni İş Talep Et")
        : (todayTaskCount >= maxTaskCount && maxTaskCount > 0)
            ? "Günlük limit doldu"
            : (isAssigningTask ? "İş atanıyor..." : "Yeni İş Talep Et");

    return (
        <Fragment>
            <Breadcrumbs parent='Sayfalar' title='İş Listesi' />
            <Container fluid>
                <Card>
                    <div className="d-flex flex-column flex-lg-row justify-content-lg-between align-items-lg-center w-100 p-3 p-md-4 gap-lg-5">
                        {/* Navigation Tabs */}
                        <Nav className='nav-primary nav-pills d-flex flex-wrap mb-4 mb-lg-0 flex-grow-1 justify-content-center'>
                            {visibleTabs.map((category) => {
                                const categoryIndex = TodoCategoriesList.indexOf(category);
                                const tabId = (categoryIndex + 1).toString();
                                const isActive = activeTab === tabId;

                                // Determine the badge count based on the active tab
                                let count = '-'; // Default for inactive tabs
                                let isLoading = false;

                                if (isActive) {
                                    if (tabId === '2') { // Tab 2 (Yeni Eklenenler)
                                        isLoading = isOwnTasksLoading;
                                        count = ownTasksTotalRows;
                                    } else if (tabId === '7') { // Tab 7 (Tahkime Hazırlar)
                                        isLoading = isArbitrationLoading;
                                        count = arbitrationTotalRows;
                                    } else if (tabId === '8') { // Tab 8 (Tahkimdekiler)
                                        isLoading = isArbitrationActiveLoading;
                                        count = arbitrationActiveTotalRows;
                                    } else if (tabId === '9') { // Tab 9 (Kapalı Dosyalar)
                                        isLoading = isClosedFilesLoading;
                                        count = closedFilesTotalRows;
                                    } else { // Other active tabs
                                        isLoading = isActiveListLoading;
                                        count = activeListCount;
                                    }
                                }

                                // Show '...' if the active tab's count is loading
                                if (isActive && isLoading) {
                                    count = '...';
                                }

                                return (
                                    <NavItem key={categoryIndex} className="mb-2 mb-md-0 me-2 my-2" style={{ width: '220px' }}>
                                        <NavLink
                                            role="button" tabIndex="0"
                                            className={classnames('d-flex align-items-center px-3 py-2 h-100', { 'active': isActive, 'bg-light': !isActive, 'text-dark': !isActive })}
                                            onClick={() => { toggle(tabId); }}
                                            style={{ borderRadius: '0.375rem' }}
                                        >
                                            <div className="d-flex align-items-center w-100 justify-content-between">
                                                <div className="d-flex align-items-center">
                                                    <span className={`d-inline-flex align-items-center ${isActive ? 'text-white' : `text-${category.color}`}`}>
                                                        {category.icon}
                                                    </span>
                                                    <span className="ms-2">{category.title}</span>
                                                </div>
                                                {/* Dynamic Badge: Shows count for active tab, '-' otherwise */}
                                                <span
                                                    className={`badge ${isActive ? 'bg-white text-primary' : `bg-${category.color}`} rounded-circle d-inline-flex align-items-center justify-content-center`}
                                                    style={{ minWidth: '24px', height: '24px', fontSize: count === '...' || count === '-' ? '0.7em' : '1em', padding: '0 5px' }}
                                                >
                                                    {count}
                                                </span>
                                            </div>
                                        </NavLink>
                                    </NavItem>
                                );
                            })}
                        </Nav>

                        {/* Counts and Buttons (only for users who can request tasks) */}
                        {canRequestTask && (
                            <div className="d-flex flex-column flex-lg-row align-items-lg-center gap-3 mt-3 mt-lg-0 flex-shrink-0">
                                <div className="d-flex gap-3 justify-content-center text-center text-lg-start">
                                    {(userRoleConfig.taskType === 'appraiser' || userRoleConfig.taskType === 'headAppraiser') ? (
                                        isAppraiserCountLoading ? (
                                            <small className="text-muted">Yükleniyor...</small>
                                        ) : (
                                            <>
                                                <div className='border p-2 rounded bg-light'>
                                                    <small className="text-muted d-block">Atanan İş</small>
                                                    <span className="fw-bold fs-6 text-dark">{appraiserTaskCount}</span>
                                                </div>
                                                <div className='border p-2 rounded bg-light'>
                                                    <small className="text-muted d-block">Havuzdaki İş</small>
                                                    <span className="fw-bold fs-6 text-dark">{poolTaskCount}</span>
                                                </div>
                                            </>
                                        )
                                    ) : (
                                        isCountLoading ? (
                                            <small className="text-muted">Yükleniyor...</small>
                                        ) : (
                                            <>
                                                <div className='border p-2 rounded bg-light'>
                                                    <small className="text-muted d-block">Bugün Alınan</small>
                                                    <span className="fw-bold fs-6 text-dark">{todayTaskCount}</span>
                                                </div>
                                                <div className='border p-2 rounded bg-light'>
                                                    <small className="text-muted d-block">Maks. Talep</small>
                                                    <span className="fw-bold fs-6 text-dark">{maxTaskCount}</span>
                                                </div>
                                            </>
                                        )
                                    )}
                                </div>
                                <div className="d-flex flex-column align-items-stretch gap-2">
                                    <Btn attrBtn={{
                                        color: 'outline-primary d-flex align-items-center justify-content-center',
                                        onClick: handleRequestNewJobClick,
                                        style: { backgroundColor: '#9188f521', border: '1px solid #9188f540' },
                                        disabled: assignButtonDisabled,
                                        title: assignButtonTitle
                                    }}>
                                        {isAssigningTask ? (<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>) : (<Plus style={{ width: '20px', height: '20px' }} className='me-2' />)}
                                        {assignButtonTitle}
                                    </Btn>
                                    <Btn attrBtn={{ color: 'outline-info d-flex align-items-center justify-content-center', onClick: toggleInfo, style: { backgroundColor: '#57daff1f', border: '1px solid #57daff40' }, }}>
                                        <Info style={{ width: '20px', height: '20px' }} className='me-2' /> Bilgi
                                    </Btn>
                                </div>
                            </div>
                        )}
                    </div>
                </Card>
                <Card>
                    <CardBody>
                        <TabContent activeTab={activeTab}>
                            {visibleTabs.map((category) => {
                                const categoryIndex = TodoCategoriesList.indexOf(category);
                                const tabId = (categoryIndex + 1).toString();

                                // Tab 2 renders Own Tasks content
                                if (tabId === '2') {
                                    return (
                                        <TabPane tabId={tabId} key={categoryIndex}>
                                            {activeTab === tabId && renderOwnTasksContent()}
                                        </TabPane>
                                    );
                                }

                                // Tab 7 renders Arbitration content
                                if (tabId === '7') {
                                    return (
                                        <TabPane tabId={tabId} key={categoryIndex}>
                                            {activeTab === tabId && renderArbitrationContent()}
                                        </TabPane>
                                    );
                                }

                                // Tab 8 renders Arbitration Active content
                                if (tabId === '8') {
                                    return (
                                        <TabPane tabId={tabId} key={categoryIndex}>
                                            {activeTab === tabId && renderArbitrationActiveContent()}
                                        </TabPane>
                                    );
                                }

                                // Tab 9 renders Closed Files content
                                if (tabId === '9') {
                                    return (
                                        <TabPane tabId={tabId} key={categoryIndex}>
                                            {activeTab === tabId && renderClosedFilesContent()}
                                        </TabPane>
                                    );
                                }

                                // Other tabs render TodoListListing, passing the count callback
                                return (
                                    <TabPane tabId={tabId} key={categoryIndex}>
                                        {activeTab === tabId && (
                                            <TodoListListing
                                                key={`list-${tabId}-${listRefreshKey}`}
                                                onCountChange={handleListCountChange} // Pass callback down
                                                activeTabId={tabId} // Pass active tab ID
                                            />
                                        )}
                                    </TabPane>
                                );
                            })}
                        </TabContent>
                    </CardBody>
                </Card>
                <Modal isOpen={isInfoOpen} toggle={toggleInfo}>
                    <ModalHeader toggle={toggleInfo}>Sekme Bilgileri</ModalHeader>
                    <ModalBody>
                        {Object.keys(tabInfo).map(key => (
                            <div key={key} className="mb-3">
                                <h6 style={{ color: tabInfo[key].color, fontWeight: 'bold' }}>{tabInfo[key].title}</h6>
                                <p className="mb-0">{tabInfo[key].content}</p>
                            </div>
                        ))}
                    </ModalBody>
                    <ModalFooter>
                        <Btn attrBtn={{ color: 'secondary', onClick: toggleInfo }}>Kapat</Btn>
                    </ModalFooter>
                </Modal>

                {/* Dosya Kapa Modal */}
                <Modal isOpen={isCloseFileModalOpen} toggle={closeFileModal}>
                    <ModalHeader toggle={closeFileModal}>Dosya Kapatma</ModalHeader>
                    <ModalBody>
                        <p className="text-muted mb-3">
                            Bu işlem dosyayı kapalı duruma getirecektir. Lütfen kapanma açıklamasını seçiniz.
                        </p>
                        <FormGroup>
                            <Label for="fileCloseDescription">Kapanma Açıklaması <span className="text-danger">*</span></Label>
                            <Input
                                type="select"
                                id="fileCloseDescription"
                                value={fileCloseDescription}
                                onChange={(e) => setFileCloseDescription(e.target.value)}
                            >
                                <option value="">Kapanma açıklaması seçin...</option>
                                <option value="Gelen ödeme yeterli">Gelen ödeme yeterli</option>
                                <option value="Aynı yerden eski hasarlı">Aynı yerden eski hasarlı</option>
                                <option value="Ağır hasarlı">Ağır hasarlı</option>
                                <option value="Farklı vekil ödeme almış">Farklı vekil ödeme almış</option>
                                <option value="Farklı vekil tahkime başvuru yapmış">Farklı vekil tahkime başvuru yapmış</option>
                                <option value="Vekalet süresi bitti">Vekalet süresi bitti</option>
                                <option value="Azil">Azil</option>
                                <option value="Teminat limiti dolu">Teminat limiti dolu</option>
                                <option value="Sulh">Sulh</option>
                                <option value="Geri çekme">Geri çekme</option>
                                <option value="Feragat">Feragat</option>
                                <option value="Bilirkişi ücreti yatırılmadı">Bilirkişi ücreti yatırılmadı</option>
                                <option value="Kusur değişmedi">Kusur değişmedi</option>
                                <option value="Bakiye limit çıkmadı">Bakiye limit çıkmadı</option>
                                <option value="Görevsizlik">Görevsizlik</option>
                                <option value="Kısmi dava açıldığından">Kısmi dava açıldığından</option>
                                <option value="Fatura olmadığından">Fatura olmadığından</option>
                                <option value="Derdestlik">Derdestlik</option>
                                <option value="Zaman aşımı">Zaman aşımı</option>
                                <option value="Yanlış başvuru">Yanlış başvuru</option>
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label for="fileCloseComment">Ek Yorum <span className="text-danger">*</span></Label>
                            <Input
                                type="textarea"
                                id="fileCloseComment"
                                value={fileCloseComment}
                                onChange={(e) => setFileCloseComment(e.target.value)}
                                placeholder="Dosya kapatma ile ilgili ek yorumlarınızı buraya yazın..."
                                rows="3"
                            />
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Btn attrBtn={{ color: 'secondary', onClick: closeFileModal, disabled: isClosingFile }}>
                            İptal
                        </Btn>
                        <Btn attrBtn={{
                            color: 'danger',
                            onClick: handleCloseFile,
                            disabled: isClosingFile || !fileCloseDescription.trim() || !fileCloseComment.trim()
                        }}>
                            {isClosingFile ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Dosya Kapatılıyor...
                                </>
                            ) : (
                                'Dosyayı Kapat'
                            )}
                        </Btn>
                    </ModalFooter>
                </Modal>

                {/* Başvuru Ücreti Modal */}
                <Modal isOpen={isApplicationFeeModalOpen} toggle={closeApplicationFeeModal}>
                    <ModalHeader toggle={closeApplicationFeeModal}>Başvuru Ücreti Gir</ModalHeader>
                    <ModalBody>
                        <FormGroup>
                            <Label for="arbitrationApplicationFee">Başvuru Ücreti (TL) <span className="text-danger">*</span></Label>
                            <Input
                                type="text"
                                id="arbitrationApplicationFee"
                                placeholder="Başvuru ücreti miktarını giriniz..."
                                value={arbitrationApplicationFee}
                                onChange={(e) => {
                                    // Sadece sayı girişine izin ver
                                    const value = e.target.value.replace(/[^0-9]/g, '');
                                    setArbitrationApplicationFee(value);
                                }}
                            />
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Btn attrBtn={{ color: 'secondary', onClick: closeApplicationFeeModal, disabled: isSubmittingApplicationFee }}>
                            İptal
                        </Btn>
                        <Btn attrBtn={{
                            color: 'primary',
                            onClick: handleSubmitApplicationFee,
                            disabled: isSubmittingApplicationFee || !arbitrationApplicationFee.trim()
                        }}>
                            {isSubmittingApplicationFee ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Kaydediliyor...
                                </>
                            ) : (
                                'Kaydet'
                            )}
                        </Btn>
                    </ModalFooter>
                </Modal>

                {/* Tahkim Bilgileri Modal */}
                <Modal isOpen={isArbitrationInfoModalOpen} toggle={closeArbitrationInfoModal} size="lg">
                    <ModalHeader toggle={closeArbitrationInfoModal}>Tahkim Bilgileri Gir</ModalHeader>
                    <ModalBody>
                        <Row>
                            <Col md="6">
                                <FormGroup>
                                    <Label for="arbitrationApplicationDate">Başvuru Tarihi <span className="text-danger">*</span></Label>
                                    <Input
                                        type="datetime-local"
                                        id="arbitrationApplicationDate"
                                        value={arbitrationApplicationDate}
                                        onChange={(e) => setArbitrationApplicationDate(e.target.value)}
                                        required
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="6">
                                <FormGroup>
                                    <Label for="arbitrationCaseNumber">Esas Numarası <span className="text-danger">*</span></Label>
                                    <Input
                                        type="text"
                                        id="arbitrationCaseNumber"
                                        value={arbitrationCaseNumber}
                                        onChange={(e) => setArbitrationCaseNumber(e.target.value)}
                                        placeholder="Esas numarasını giriniz..."
                                        required
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md="12">
                                <FormGroup>
                                    <Label for="arbitrationPriceForNow">Şimdilik Tutarı</Label>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        id="arbitrationPriceForNow"
                                        value={arbitrationPriceForNow}
                                        onChange={(e) => setArbitrationPriceForNow(e.target.value)}
                                        placeholder="Tutarı giriniz..."
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    </ModalBody>
                    <ModalFooter>
                        <Btn attrBtn={{ color: 'secondary', onClick: closeArbitrationInfoModal }}>
                            İptal
                        </Btn>
                        <Btn
                            attrBtn={{
                                color: 'primary',
                                onClick: handleSubmitArbitrationInfo,
                                disabled: isSubmittingArbitrationInfo || !arbitrationApplicationDate || !arbitrationCaseNumber
                            }}
                        >
                            {isSubmittingArbitrationInfo ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Kaydediliyor...
                                </>
                            ) : (
                                'Kaydet'
                            )}
                        </Btn>
                    </ModalFooter>
                </Modal>

                {/* Arbitration Expense Modal */}
                {currentArbitrationItem && (
                    <ExpenseDataModal
                        apiEndpoint={process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/expense'}
                        isOpen={isArbitrationExpenseModalOpen}
                        toggle={closeArbitrationExpenseModal}
                        mode="add"
                        selectedExpense={null}
                        refreshData={refreshArbitrationActiveData}
                        customerId={null}
                        autoAssignCustomer={false}
                        accidentFile={[]}
                        callformId={null}
                        readOnlyView={false}
                        relatedArbitrationSchema={currentArbitrationItem.arbitrationInformation?.id ? [String(currentArbitrationItem.arbitrationInformation.id)] : []}
                        arbitrationSchemaType="Tahkim Başvuru Ücreti Ödemesi"
                    />
                )}

                {/* File Close Modal */}
                <FileCloseModal
                    isOpen={isFileCloseModalOpen}
                    toggle={closeFileCloseModal}
                    arbitrationId={currentArbitrationIdForClose}
                    refreshArbitrationData={refreshArbitrationDataAfterClose}
                />
            </Container>
        </Fragment>
    );
};

export default TodoList;
